import { flowRecordingPath } from '../../../flow-v2/sessions/recording/recording-session-link';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  SecurityContext,
  signal,
  untracked,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  type ConnectedPosition,
} from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { BookMeetingService, RoomSelection, LinkedCateringOrder } from '../../../../core/services/book-meeting.service';
import { MeetingDraftService } from '../../../../core/services/meeting-draft.service';
import { MeetingPanelService } from '../../../../core/services/meeting-panel.service';
import { scheduleLinkForMeeting } from '../../../../core/utils/schedule-link';
import { RealtimeService } from '../../../../core/services/realtime.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import {
  ACTIONABLE_INSIGHT_HEADINGS,
  BookMeetingView,
  FlowNoteVariant,
  FlowRevealSection,
  FlowSpeakerSource,
} from '../../../../core/constants/enums';
import { CateringOrderDraft } from '../../../../core/models/catering.model';
import { ServiceRequestDraft } from '../../../../core/models/service-request.model';
import { formatCurrencyMinor } from '../../../../core/utils/catering';
import { formatSecondsMinSec } from '../../../../core/utils/time';
import { CateringVendorService } from '../../../../core/services/catering-vendor.service';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { environment } from '../../../../../environments/environment';
import { MeetingDetailsSectionComponent } from './sections/meeting-details-section.component';
import { AttendeesSectionComponent } from './sections/attendees-section.component';
import { ConfirmationSectionComponent } from './sections/confirmation-section.component';
import { VendorListComponent } from '../../../catering/components/vendor-list/vendor-list.component';
import { MenuBrowseComponent } from '../../../catering/components/menu-browse/menu-browse.component';
import { CartSummaryComponent } from '../../../catering/components/cart-summary/cart-summary.component';
import { EmailRequestFormComponent } from '../../../catering/components/email-request-form/email-request-form.component';
import { MoreOptionsSectionComponent } from './sections/more-options-section.component';
import { ConferencingSectionComponent } from './sections/conferencing-section.component';
import { DateTimeSectionComponent } from './sections/date-time-section.component';
import { RoomPickerInlineComponent } from './sections/room-picker-inline.component';
import { RoomServicesSectionComponent, RoomServices } from './sections/room-services-section.component';
import { BmpTabBarComponent, type BmpTabId, type BmpTabCounts, type BmpTabValidation } from './sections/bmp-tab-bar.component';
import { IntentHintChipComponent } from '../scheduling-dialog/sections/intent-hint-chip/intent-hint-chip';
import { NotesSectionComponent } from './sections/notes-section.component';
// IM-KEEP: building-alert banner mounted in book-meeting-panel.component.html
import { PaperAlertsBannerComponent } from '../../../../shared/paper-app/paper-alerts-banner';
import { BuildingAlertsService, type BuildingAlert } from '../../../../core/services/building-alerts.service';
// Schedule V2 atoms — surface the new prototype affordances inline.
import { BmpClassifyRowComponent } from './sections/bmp-classify-row.component';
import { BmpTemplatesRowComponent } from './sections/bmp-templates-row.component';
import {
  BmpLayoutPickerComponent,
  type LayoutKey,
} from './sections/bmp-layout-picker.component';
import { BmpAiSideComponent } from './sections/bmp-ai-side.component';
import { BmpFooterSummaryComponent } from './sections/bmp-footer-summary.component';
import { BmpVisitorsSectionComponent } from './sections/bmp-visitors-section.component';
import { BmpVisitorsOutcomeComponent } from './sections/bmp-visitors-outcome.component';
import { MeetingVisitorsService } from '../../../../core/services/meeting-visitors.service';
import { HostVisitsService } from '../../../../core/services/host-visits.service';
import { FeatureFlagsViewerService } from '../../../../core/services/feature-flags-viewer.service';
import {
  BmpFindTimeStripComponent,
  type FindTimeAttendee,
  type FindTimeBetterSlot,
} from './sections/bmp-find-time-strip.component';
import {
  type MeetingTemplate,
  UserLibraryService,
} from '../../../../core/services/user-library.service';
import type {
  RoomsGroupUsesRoom,
  SimilarMeeting,
} from '../../../../core/services/meeting-intelligence.service';
import { ServiceCardComponent } from '../../../catering/components/service-card/service-card.component';
import { PlatformDataService } from '../../../../core/services/platform-data.service';
import { ProfileDataService } from '../../../../core/services/profile-data.service';
import { FlowProjectsService } from '../../../../core/services/flow-projects.service';
import { WorkspaceStateService } from '../../../../core/services/workspace-state.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { SchedulingAssistantService } from '../../../../core/services/scheduling-assistant.service';
import { LocationStateService } from '../../../../core/services/location-state.service';
import {
  PaperIconComponent,
  PaperButtonComponent,
  PaperDialogComponent,
} from '@ricoh-paper/angular';
import { ImFeatureFlagDirective } from '../../../../shared/directives/im-feature-flag.directive';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { ROOM_LAYOUTS } from '../../../../core/constants/meeting-services';
import { ActionShelfComponent } from '../space-detail-panel/sections/action-shelf/action-shelf';
import { ViewerIdentityService } from '../../../../core/services/viewer-identity.service';
import { parseTeamsBody, MentionAttendee } from '../../../../core/utils/meeting-body-parser';
import { getInitials } from '../../../../core/utils/text';
import { withClock } from '../../../../core/utils/format-prefs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../core/services/locale.service';
import {
  buildCategoryEyebrow,
  categoryHue,
  categoryLabel,
} from '../../../../core/constants/meeting-categories';
import type {
  FlowMeetingContent,
  FlowTranscriptSegment,
} from '../../../../core/services/book-meeting.service';

/**
 * One row in the speaker rename popover. Surfaces meeting attendees
 * + the broader employee directory under a single shape so the
 * template doesn't need to branch on source. `assignedUserId` and
 * `assignedUserEmail` are passed through to the backend so the
 * speaker_map[] entry can be later clustered by user identity for
 * voice training.
 */
interface SpeakerSuggestion {
  id: string;
  name: string;
  email?: string | null;
  subtitle?: string | null;
  avatar?: string | null;
  assignedUserId?: string | null;
  assignedUserEmail?: string | null;
}

/**
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║ RETIRING — DO NOT ADD FEATURES HERE.                                   ║
 * ║                                                                        ║
 * ║ The right-hand Book a Meeting panel is being decommissioned. It stays  ║
 * ║ live only as a TRANSITIONAL fallback (tenants without `rooms.booking`, ║
 * ║ and the legacy schedule). Add meeting features to:                     ║
 * ║   • the /schedule event panel — `sp-meeting-card` (bottom-right) and   ║
 * ║     `sp-meeting-expanded` (full screen), hosted by                     ║
 * ║     `SchedulePreviewComponent` (features/schedule-preview/);           ║
 * ║   • the Calendar (Beta) side panel — `sv3-event-editor` /              ║
 * ║     `sv3-event-detail` (features/schedule-v3/).                        ║
 * ║ Put reusable logic in a service (e.g. `MeetingVisitorsService`) so it  ║
 * ║ serves those surfaces, not in this component. Bug fixes only.          ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 *
 * BookMeetingPanelComponent — slide-in panel for creating room bookings
 * with catering, attendees, and services.
 *
 * Multi-view panel: Form → VendorBrowse → MenuBrowse → CartReview → Confirmation.
 * Time is always pre-populated from the launch context (schedule grid, space panel, etc.).
 */
@Component({
  selector: 'im-book-meeting-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MeetingDetailsSectionComponent,
    AttendeesSectionComponent,
    ConfirmationSectionComponent,
    VendorListComponent,
    MenuBrowseComponent,
    CartSummaryComponent,
    EmailRequestFormComponent,
    MoreOptionsSectionComponent,
    RoomPickerInlineComponent,
    RoomServicesSectionComponent,
    ServiceCardComponent,
    PaperIconComponent,
    DecimalPipe,
    FormsModule,
    ConferencingSectionComponent,
    DateTimeSectionComponent,
    ActionShelfComponent,
    PaperButtonComponent,
    BmpTabBarComponent,
    IntentHintChipComponent,
    NotesSectionComponent,
    BmpClassifyRowComponent,
    BmpTemplatesRowComponent,
    BmpAiSideComponent,
    BmpFooterSummaryComponent,
    BmpVisitorsSectionComponent,
    BmpVisitorsOutcomeComponent,
    BmpFindTimeStripComponent,
    BmpLayoutPickerComponent,
    PaperDialogComponent,
    ImFeatureFlagDirective,
    PaperAlertsBannerComponent,
    // BmpAttendeeChipComponent is imported
    // and wired through their handlers, but the inline-in-room-card
    // and inline-in-attendee-row integration lives in the next pass
    // (see beads issues 5, 8). They're kept in the file for cheap
    // resolution when the integration lands.,
    TranslocoPipe,
  ],
  templateUrl: './book-meeting-panel.component.html',
  styleUrl: './book-meeting-panel.component.scss',
})
export class BookMeetingPanelComponent implements AfterViewInit, OnDestroy {
  private static readonly STORAGE_KEY = 'im-book-meeting-panel-width';
  private static readonly MIN_WIDTH = 380;
  /** Max panel width — bumped from 800 to 1280 to support the V2
   *  two-column layout (form left + AI side rail right). */
  private static readonly MAX_WIDTH = 1280;
  /** Legacy default — 440px single column. */
  private static readonly DEFAULT_WIDTH = 440;
  /** V2 default — 1080px two-column with the AI rail. Used when
   *  `scheduleV2Enabled` AND the panel is in create/edit mode (the
   *  view-mode hero still wants the narrow column). */
  private static readonly V2_DEFAULT_WIDTH = 1080;

  // ── Flow card tunables ──────────────────────────────
  /** Max suggestions shown per section in the speaker rename popover. */
  private static readonly SUGGESTION_LIMIT = 5;
  /** Max avatars stacked in the Flow card header. */
  private static readonly AVATAR_STACK_LIMIT = 3;
  /** How long the per-action-item Copy chip flips to "Copied". */
  private static readonly COPY_FEEDBACK_MS = 2000;

  protected readonly service = inject(BookMeetingService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly identity = inject(ViewerIdentityService);
  private readonly zone = inject(NgZone);
  private readonly vendorService = inject(CateringVendorService);
  private readonly cartService = inject(CateringCartService);
  private readonly platformData = inject(PlatformDataService);
  private readonly profileData = inject(ProfileDataService);
  protected readonly flowProjects = inject(FlowProjectsService);
  protected readonly workspaceState = inject(WorkspaceStateService);
  private readonly shellState = inject(ShellStateService);
  private readonly scheduling = inject(SchedulingAssistantService);
  private readonly locationState = inject(LocationStateService);
  // IM-KEEP: drives meetingAlerts() consumed by the alerts banner.
  private readonly buildingAlerts = inject(BuildingAlertsService);
  private readonly el = inject(ElementRef);
  /** The floating "Open in schedule / Replan" shortcuts navigate. */
  private readonly router = inject(Router);
  private readonly meetingPanel = inject(MeetingPanelService);

  /** IM-KEEP: alerts overlapping the primary room + meeting window. */
  protected readonly meetingAlerts = signal<BuildingAlert[]>([]);
  private meetingAlertsDebounce: ReturnType<typeof setTimeout> | null = null;
  /** Schedule V2 — viewer-owned library (categories, tags, templates). */
  private readonly userLibrary = inject(UserLibraryService);
  /** Schedule V2 — server-synced minimised-draft store. The dock
   *  (BmpDraftDockComponent) renders chips from the same signal; this
   *  panel PUSHES drafts (autosave + minimise) but no longer restores
   *  them — resumes open the schedule redesign's creation panel. */
  private readonly meetingDrafts = inject(MeetingDraftService);
  private readonly realtime = inject(RealtimeService);
  private readonly analytics = inject(AnalyticsService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Schedule V2 feature flag — gates the new templates row / classify
   *  chips / AI side rail / find-time strip / footer summary. Defaults
   *  on in dev, can be flipped off via `NG_APP_SCHEDULE_V2_ENABLED=false`
   *  during prod rollout. */
  protected readonly scheduleV2Enabled = environment.featureFlags.scheduleV2Enabled;

  /** Convenience computed — view mode is the dominant gate across the
   *  template (chrome, banners, footer, body branch). Cheap and reads
   *  better than `service.mode() === 'view'` everywhere. */
  protected readonly isView = computed(() => this.service.mode() === 'view');

  /** Visitors on the booking — see `BmpVisitorsSectionComponent`. */
  protected readonly meetingVisitors = inject(MeetingVisitorsService);
  private readonly hostVisits = inject(HostVisitsService);
  private readonly visitorsFlag = inject(FeatureFlagsViewerService).isEnabled('visitors');
  /**
   * Behind the `visitors` flag. Creating needs `visit:create`; viewing needs
   * nothing more than the default own-visit read, which only ever returns
   * the viewer's own visitors on this booking.
   */
  protected readonly showVisitors = computed(
    () =>
      this.visitorsFlag() &&
      (this.isView() || this.hostVisits.capabilities().canInvite),
  );

  /** Editing an EXISTING meeting — the header must never present the
   *  create-flavoured chrome ("New meeting" fallback + draft autosave
   *  pill) for a meeting that already exists. */
  protected readonly isEdit = computed(() => this.service.mode() === 'edit');

  /** Unsaved edit-mode changes (the service's snapshot diff) — drives the
   *  "Editing · unsaved changes" header pill. */
  protected readonly editDirty = computed(() => this.service.dirty());

  // ── Schedule shortcuts (floating cluster on the panel's left edge) ──

  /** True when the loaded meeting can be handed to the schedule page —
   *  the shortcuts hide for meetings with no resolvable id. */
  protected readonly canOpenInSchedule = computed(
    () =>
      this.isView() &&
      !!(this.service.loadedBookingId() ?? this.service.loadedMeetingId()),
  );

  /**
   * "Open in schedule" / "Replan · find a time" — navigate to the
   * redesigned schedule with this meeting's deep-link params (day + time
   * + open detail; replan additionally pins the attendees onto
   * Find-a-time). Works from ANY page the panel is open over; a `k`
   * nonce forces the schedule to reprocess when it's already the active
   * route. The panel closes — the schedule takes over the context.
   */
  protected openInSchedule(intent: 'view' | 'replan'): void {
    const id = this.service.loadedBookingId() ?? this.service.loadedMeetingId();
    if (!id) return;
    const params = scheduleLinkForMeeting({
      id,
      start: this.service.startTime(),
      timezone: this.locationState.timezone(),
      intent: intent === 'replan' ? 'replan' : undefined,
    });
    this.analytics.capture(ANALYTICS_EVENTS.BMP_OPEN_IN_SCHEDULE_CLICKED, {
      meeting_id: id,
      intent,
      from_route: this.router.url.split('?')[0],
    });
    const start = this.service.startTime();
    this.service.close('route-change');
    // The meeting panel, in place, where the tenant has it: no page change, and Replan opens on Availability.
    // A tenant without `rooms.booking` (the one that reaches this panel) keeps the link.
    if (this.meetingPanel.available() && this.meetingPanel.openMeeting(id, start ? start.toISOString() : null, {
      section: intent === 'replan' ? 'edit' : 'details',
      door: intent === 'replan' ? 'bmp-replan' : 'bmp-open',
      ...(intent === 'replan' ? { tab: 'availability' as const } : {}),
    })) return;
    void this.router.navigate(['/schedule'], {
      queryParams: { ...params, k: Date.now().toString(36) },
    });
  }

  /** Logged-in viewer record. Drives the bottom-left identity card in view
   *  mode and the "You" badge on the matching attendee row. Returns null
   *  during early bootstrap before the platform-data load resolves — every
   *  consumer must gate on truthiness. */
  protected readonly viewer = this.identity.viewer;

  /** Initials for the viewer card avatar — falls back to "?" when the name
   *  is blank or only whitespace, mirroring the parser's helper. */
  protected readonly viewerInitials = computed(() => {
    const v = this.viewer();
    return getInitials(v?.name ?? v?.email ?? '');
  });

  /**
   * Live availability lookup for a room currently on the meeting — reads
   * PlatformDataService.availabilityMap (signal), which RealtimeService
   * updates over WebSocket as bookings change. Template binds via
   * `roomAvailability(spaceId)` so the badge re-renders without polling
   * if the room becomes busy while the panel is open.
   */
  protected roomAvailability(spaceId: string): {
    status: 'available' | 'busy' | 'unknown';
    label: string;
  } {
    const avail = this.platformData.getSpaceAvailability(spaceId);
    if (!avail) return { status: 'unknown', label: '' };
    if (avail.availableAtTime) {
      return { status: 'available', label: 'Available' };
    }
    return {
      status: 'busy',
      label: avail.hasBooking ? 'Now in use' : 'Unavailable',
    };
  }

  protected readonly visible = this.service.visible;
  protected readonly view = this.service.view;
  protected readonly BookMeetingView = BookMeetingView;

  /**
   * Active tab on the create/edit form. The tab bar is hidden in view
   * mode (which has its own polished hero-style layout) so this signal
   * only matters when the user is filling in the form.
   *
   * Default = 'meeting' for new bookings; 'rooms' when an existing
   * meeting loads without a room assigned (the most common reason
   * someone re-opens an edit). All other edit cases land on 'meeting'.
   */
  protected readonly activeTab = signal<BmpTabId>('meeting');

  /** Count chips on each tab — purely visual, drives the tiny pill on
   *  the tab button. Reads off the service's signals so it's always in
   *  sync with the underlying state. */
  protected readonly tabCounts = computed<BmpTabCounts>(() => ({
    meeting: this.service.subject().trim() ? 1 : 0,
    rooms: this.service.rooms().length,
    people: this.service.attendees().length,
    notes: this.service.internalNotes().trim() ? 1 : 0,
    options: this.service.isOnlineMeeting() ? 1 : 0,
  }));

  /** Validation pulse — a tab pulses warning-amber when it owns a
   *  missing required field. Drives the user's eye to the right tab
   *  when they hit a disabled CTA. Empty record = no pulses.
   *
   *  Today's required fields:
   *    - Meeting: subject (we treat "Untitled meeting" as fine, so
   *      this is empty-only).
   *    - Rooms:  at least one room.
   *    - People: at least one attendee (the organiser always counts,
   *      so this only fires defensively when the service hasn't yet
   *      hydrated the organiser).
   *    - Options: no required fields. */
  protected readonly tabValidation = computed<BmpTabValidation>(() => {
    const validation: BmpTabValidation = {};
    if (!this.service.subject().trim()) validation.meeting = true;
    if (this.service.rooms().length === 0) validation.rooms = true;
    if (this.service.attendees().length === 0) validation.people = true;
    // Conflict pulse on People — soft signal so the user notices a
    // calendar clash even when the required field (≥1 attendee) is met.
    //
    // An UNRESOLVED probe pulses too, and deliberately. Silence here is not
    // neutral: it is read as "no clashes", which is the one thing we do not
    // know. Both states want the same response from the person — go and look
    // at the People tab — so both get the same nudge. It does not assert a
    // conflict exists; it stops us asserting one does not.
    if (this.conflictCount() > 0 || this.conflictProbeUnresolved()) validation.people = true;
    return validation;
  });

  /** True when the tab bar should render — only in the form view and
   *  only when the user is creating/editing, never in view mode. */
  protected readonly showTabBar = computed(() =>
    this.view() === BookMeetingView.FORM && !this.isView(),
  );

  /**
   * Tab clicked — smooth-scroll the panel body to the matching section
   * anchor. The active tab updates immediately so the indicator slides
   * before the scroll completes; the scroll-spy is suppressed for ~600ms
   * after a programmatic click so the in-flight animation doesn't fight
   * the spy's "what's at the top right now" logic.
   */
  protected onTabChanged(id: BmpTabId): void {
    this.activeTab.set(id);
    // Reset fractional progress so the indicator snaps directly to
    // the new tab's geometry rather than briefly easing across from
    // the old position during the smooth-scroll.
    this.scrollProgress.set(0);
    this.suppressScrollSpyUntil = Date.now() + 600;
    requestAnimationFrame(() => {
      const body = this.scrollContainer();
      const target = body?.querySelector(`#bmp-sec-${id}`) as HTMLElement | null;
      if (!body || !target) return;
      // Allow the sticky tab bar to overlap the target by 4px so the
      // section heading isn't clipped under the bar after scroll.
      const offset = this.stickyBarHeight();
      body.scrollTo({
        top: target.offsetTop - offset - 4,
        behavior: 'smooth',
      });
    });
  }

  // ── Scroll spy ───────────────────────────────────────
  //
  // The Book Meeting form is a single scroll container; the tab bar
  // sits sticky at the top and reflects whichever section the user has
  // scrolled past. Mirrors the polished `space-detail-panel` pattern.
  //
  // Implementation: RAF-throttled scroll listener on `.bmp__content`
  // that finds the section whose top has just crossed the sticky bar's
  // bottom edge. Cheap (~4 elements queried per frame), simple, and
  // matches the user's mental model of "tabs jump to where you are".

  private scrollSpyRaf: number | null = null;
  /** Timestamp until which the scroll-spy is suppressed (after a
   *  programmatic click). Avoids fighting the in-flight smooth scroll. */
  private suppressScrollSpyUntil = 0;
  private scrollListener: (() => void) | null = null;

  /**
   * Fractional progress (0…1) from the active section toward the NEXT
   * section. Passed to the tab bar so the indicator slides smoothly
   * as the user scrolls, rather than jumping when `activeTab` flips.
   * Recomputed per scroll frame.
   */
  protected readonly scrollProgress = signal(0);

  ngAfterViewInit(): void {
    // Keep the "Saved 4s ago" copy fresh — tick every 5 seconds.
    // Cheaper than every-second and matches the granularity the user
    // can perceive in the header chip.
    this.savedTickInterval = setInterval(
      () => this.savedTick.update(t => t + 1),
      5_000,
    );
    // The scroll container is the `.bmp__content` div — that's where
    // the form sections live. Sticky tab bar at the top stays put;
    // sections beneath scroll under it.
    const body = this.scrollContainer();
    if (!body) return;
    this.scrollListener = () => {
      if (this.scrollSpyRaf !== null) return;
      this.scrollSpyRaf = requestAnimationFrame(() => {
        this.scrollSpyRaf = null;
        this.updateScrollSpy();
      });
    };
    body.addEventListener('scroll', this.scrollListener, { passive: true });
    // First paint — set the initial active tab based on current scroll
    // (handles the case where the panel reopens already scrolled).
    queueMicrotask(() => this.updateScrollSpy());
  }

  private scrollContainer(): HTMLElement | null {
    return this.el.nativeElement.querySelector('.bmp__content') as HTMLElement | null;
  }

  private stickyBarHeight(): number {
    const bar = this.el.nativeElement.querySelector('bmp-tab-bar') as HTMLElement | null;
    return bar?.offsetHeight ?? 0;
  }

  /**
   * Find which section anchor is currently "active" — i.e. the one
   * whose top has scrolled to (or past) the sticky tab bar's bottom
   * edge. Update `activeTab()` if it changed, and compute the
   * fractional progress from the active section to the next one so
   * the tab indicator can slide smoothly as the user scrolls.
   */
  private updateScrollSpy(): void {
    if (!this.showTabBar()) return;
    if (Date.now() < this.suppressScrollSpyUntil) return;
    const body = this.scrollContainer();
    if (!body) return;
    const bar = this.el.nativeElement.querySelector('bmp-tab-bar') as HTMLElement | null;
    const refLine = bar
      ? bar.getBoundingClientRect().bottom
      : body.getBoundingClientRect().top;

    const ids: BmpTabId[] = ['meeting', 'rooms', 'people', 'notes', 'options'];
    const positions: { id: BmpTabId; top: number }[] = [];
    for (const id of ids) {
      const el = body.querySelector(`#bmp-sec-${id}`) as HTMLElement | null;
      if (!el) continue;
      positions.push({ id, top: el.getBoundingClientRect().top - refLine });
    }
    if (positions.length === 0) return;

    // Pick the index of the last section whose top has reached or
    // passed the ref line. Defaults to 0 (first section) if nothing
    // has crossed yet — top of the form.
    let activeIdx = 0;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i].top <= 1) activeIdx = i; // 1px tolerance for sub-pixel rounding
    }
    const active = positions[activeIdx].id;
    if (this.activeTab() !== active) {
      this.activeTab.set(active);
    }

    // Fractional progress — how far between the active section's top
    // and the next section's top the scroll position has travelled.
    // The active section's top is ≤ 0 (above the ref line); the next
    // section's top is > 0 (below). When the next top reaches the ref
    // line (top = 0), progress = 1 and `activeTab` flips on the next
    // frame. Mirrors `space-detail-panel.updateScrollSpy` logic.
    let progress = 0;
    if (activeIdx < positions.length - 1) {
      const currentTop = positions[activeIdx].top;       // ≤ 0
      const nextTop = positions[activeIdx + 1].top;      // > 0 (usually)
      const range = nextTop - currentTop;
      if (range > 0) {
        progress = Math.max(0, Math.min(1, -currentTop / range));
      }
    }
    this.scrollProgress.set(progress);
  }

  /** Debounce handle for the AI subject suggestion fetch — we don't
   *  want to fire on every attendee add when the user is pasting a list
   *  of emails. 800 ms is long enough that a normal paste settles
   *  before the call, short enough that the chip feels reactive. */
  private subjectSuggestDebounce: ReturnType<typeof setTimeout> | null = null;

  /** Effect — when attendees change AND subject is blank AND we're in
   *  create mode, schedule a debounced fetch of the AI subject
   *  suggestion. Cancelled if attendees change again or the user starts
   *  typing a subject. */
  private readonly subjectSuggestEffect = effect(() => {
    const attendees = this.service.attendees();
    const subj = this.service.subject().trim();
    const mode = this.service.mode();
    // Re-read both whenever they change so the effect re-fires.
    if (mode !== 'create' || subj.length > 0) {
      this.service.aiSubjectSuggestion.set(null);
      return;
    }
    if (this.subjectSuggestDebounce) clearTimeout(this.subjectSuggestDebounce);
    if (attendees.filter(a => !a.isOrganiser).length === 0) return;
    this.subjectSuggestDebounce = setTimeout(() => {
      void this.service.loadSubjectSuggestion();
    }, 800);
  });

  /** Lazy-load the viewer's UserLibrary (categories + tags + templates)
   *  AND FlowProjects the first time the panel becomes visible. Both
   *  loaders are idempotent — `loadAll()` early-returns when already
   *  loaded, and `flowProjects.load()` skips when `_loaded` is true.
   *  This unblocks the classify-row's pickers (category, tag,
   *  Add-to-Flow) which show empty otherwise. Runs once per visibility
   *  transition; opening the panel a second time is a cache hit. */
  private readonly classifyLibsLoadEffect = effect(() => {
    if (!this.service.visible()) return;
    void this.userLibrary.loadAll();
    void this.flowProjects.load();
  });

  /** IM-KEEP: refresh meetingAlerts on primary room + window change. */
  private readonly meetingAlertsEffect = effect(() => {
    const primary = this.service.primaryRoom();
    const start = this.service.startTime();
    const end = this.service.endTime();
    if (!primary?.locationId || !start || !end) {
      untracked(() => this.meetingAlerts.set([]));
      return;
    }
    if (this.meetingAlertsDebounce) clearTimeout(this.meetingAlertsDebounce);
    this.meetingAlertsDebounce = setTimeout(async () => {
      // Rooms carry the meetingroom family so type-narrowed closures
      // ("all meeting rooms in Building X") overlap-match correctly.
      const list = await this.buildingAlerts.alertsOverlapping(
        {
          locationId: primary.locationId,
          floorId: primary.floorId,
          spaceId: primary.spaceId,
          spaceType: 'meetingroom',
        },
        start, end,
      );
      this.meetingAlerts.set(list);
    }, 250);
  });

  // NOTE: this panel no longer restores minimised drafts. Draft resumes
  // (dock chips, drafts library, My bookings) route to the schedule
  // redesign, whose page-level effect consumes `restoreRequest` and opens
  // the NEW creation panel — see SchedulePreviewComponent. This panel
  // still WRITES drafts (autosave below) for its own create sessions.

  // ─── Dirty tracking + debounced autosave ──────────────────────────
  //
  // The panel autosaves every 750ms after any tracked field change.
  // `dirty` flips true on every change, stays true once the optimistic
  // save lands (the chip surfaces "Saved Ns ago" via the service's
  // `lastSavedAt`), and resets to false only on a fresh `open()` or
  // a successful `submitBooking`. That's what lets the confirm-on-
  // close dialog know whether there's anything worth preserving.

  /** True when any create-mode field has changed since the last
   *  open / successful submit. Drives the confirm-on-close dialog and
   *  the autosave scheduler below. */
  protected readonly dirty = signal(false);

  /** Pending autosave timer handle — cancelled and rescheduled every
   *  time a tracked field changes so a flurry of edits coalesces into
   *  a single upsert. */
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Snapshot of the visible() value from the previous tick so the
   *  dirty-effect can detect "panel just opened" without thinking the
   *  fresh form values are user-driven edits. */
  private wasVisible = false;

  /** Effect — flips `dirty` true on any change to the create-mode
   *  fields the user could touch. Bails on the first open() tick so
   *  the freshly-reset form state doesn't immediately register as
   *  dirty. Edit mode never flips dirty (those use their own
   *  baseline/discard flow). */
  private readonly dirtyTrackerEffect = effect(() => {
    // Track every field the panel persists into a draft. Reads are
    // signal-only — the body never branches on the value.
    this.service.subject();
    this.service.startTime();
    this.service.endTime();
    this.service.attendees();
    this.service.rooms();
    this.service.description();
    this.service.meetingTags();
    this.service.meetingCategoryId();
    this.service.costCentre();
    this.service.meetingLabel();
    this.service.visibility();

    const visible = this.service.visible();
    const mode = this.service.mode();
    if (!visible) {
      if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
      this.wasVisible = false;
      return;
    }
    if (!this.wasVisible) {
      // First tick after the panel became visible — that batch is the
      // open()-seeded state, not user input. Mark visible and bail.
      this.wasVisible = true;
      return;
    }
    if (mode !== 'create') return;

    untracked(() => this.dirty.set(true));
    this.scheduleAutosave();
  });

  /** Schedule a debounced autosave 750ms in the future. Cancels any
   *  in-flight timer so a flurry of edits coalesces. The actual upsert
   *  reuses the existing draft-id strategy in `onMinimiseDraft` so the
   *  same chip updates in place rather than spawning duplicates. */
  private scheduleAutosave(): void {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      void this.persistDraftSilently();
    }, 750);
  }

  /** Promise of the in-flight autosave, if one is running. Serialises
   *  saves so a second autosave waits for the first to resolve its
   *  server id before computing its own id — without this, both calls
   *  would see `_draftId = null` (or the same temp id) and the server
   *  would create duplicate rows. */
  private autosaveInFlight: Promise<void> | null = null;

  /** Write the current panel state to MeetingDraftService without
   *  closing the panel. Reuses the same id-resolution as
   *  `onMinimiseDraft` so autosave and minimise share state. Awaits
   *  the previous save (if any) so saves serialise; updates
   *  `_draftId` from the resolved server id so the NEXT save targets
   *  the same row instead of inserting again. */
  private async persistDraftSilently(): Promise<void> {
    if (this.service.mode() !== 'create') return;

    // Serialise saves — wait for any prior save to finish so its
    // resolved server id is reflected in _draftId before we compute
    // this save's id. Without this, two autosaves fired close together
    // both observe a null _draftId and mint independent temp ids,
    // creating duplicate rows on the server.
    const prior = this.autosaveInFlight;
    const run = (async () => {
      if (prior) await prior;

      const start = this.service.startTime();
      const end = this.service.endTime();
      const title = this.service.subject().trim() || 'New meeting';
      const startIso = start?.toISOString() ?? '';

      // The panel is now mounted ONCE at app-shell level, so the prior
      // cross-mount dedupe fallback (content-match on title+startIso to
      // recover a draft id minted by a sibling panel instance) is dead
      // code. A single instance reliably tracks its own _draftId across
      // a session, with loadedMeetingId taking precedence for restored
      // drafts.
      const id = this.service.loadedMeetingId() ?? this._draftId ?? `draft-${Date.now()}`;
      this._draftId = id;

      const durationMin = start && end
        ? Math.max(15, Math.round((end.getTime() - start.getTime()) / 60_000))
        : (this.service.duration() ?? 30);
      const startMin = start
        ? start.getHours() * 60 + start.getMinutes()
        : 9 * 60;
      const dateLabel = start
        ? start.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          })
        : undefined;

      // upsert() now returns the resolved id (server's ObjectId on
      // success). Stash it on _draftId so the next autosave targets
      // the persisted row instead of minting a fresh temp id.
      const resolvedId = await this.meetingDrafts.upsert({
        id,
        title,
        startMin,
        durationMin,
        dateLabel,
        payload: {
          subject: this.service.subject(),
          description: this.service.description(),
          startTimeIso: start?.toISOString() ?? null,
          endTimeIso: end?.toISOString() ?? null,
        },
      });
      this._draftId = resolvedId;
      this.analytics.capture(ANALYTICS_EVENTS.BMP_DRAFT_SAVED, {
        trigger: 'auto',
        draft_id: resolvedId,
      });
    })();

    this.autosaveInFlight = run;
    try {
      await run;
    } finally {
      if (this.autosaveInFlight === run) this.autosaveInFlight = null;
    }
  }

  // ─── Confirm-on-close dialog state ────────────────────────────────

  /** True while the "Save this draft for later?" dialog is open. */
  protected readonly showCloseConfirm = signal(false);

  /** Ticks every 5s so the "Saved Ns ago" pill stays accurate without
   *  a re-render on every signal change. Set up in ngAfterViewInit;
   *  torn down in ngOnDestroy. */
  private readonly savedTick = signal(0);
  private savedTickInterval: ReturnType<typeof setInterval> | null = null;

  /** Live "Saving… / Saved Ns ago / Draft · autosaves" label for the
   *  header pill. */
  protected readonly draftPillLabel = computed<string>(() => {
    this.locale.locale();
    if (this.meetingDrafts.isSaving()) return this.transloco.translate('viewer.book_meeting_panel.saving');
    // Keep the tick alive so the elapsed copy refreshes.
    this.savedTick();
    const at = this.meetingDrafts.lastSavedAt();
    if (!at) return this.transloco.translate('viewer.book_meeting_panel.draft_autosaves');
    const elapsedMs = Date.now() - at.getTime();
    if (elapsedMs < 5_000) return this.transloco.translate('viewer.book_meeting_panel.saved_just_now');
    if (elapsedMs < 60_000) return this.transloco.translate('viewer.book_meeting_panel.saved_seconds_ago', { seconds: Math.round(elapsedMs / 1000) });
    const mins = Math.floor(elapsedMs / 60_000);
    if (mins === 1) return this.transloco.translate('viewer.book_meeting_panel.saved_one_min_ago');
    if (mins < 60) return this.transloco.translate('viewer.book_meeting_panel.saved_mins_ago', { minutes: mins });
    const hrs = Math.floor(mins / 60);
    return this.transloco.translate('viewer.book_meeting_panel.saved_hrs_ago', { hours: hrs });
  });

  /** Debounce for the conflict probe — same reason: paste of attendees
   *  shouldn't fire one query per email. */
  private conflictProbeDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * True when the last availability probe did not produce an answer.
   *
   * THE THIRD STATE. `conflictsByAttendee` can only say "these people are
   * busy" or "nobody is" — it has no way to say "we could not ask". Without
   * this flag a failed probe is byte-identical to a clean one, and the
   * clean one is the reassuring reading, so every outage silently became
   * "everybody is free" above a Book button.
   */
  protected readonly conflictProbeUnresolved = signal(false);

  /**
   * Effect — keep `service.conflictsByAttendee` in sync with the
   * currently selected attendees + time window. Uses the same
   * `peopleAvailability` GraphQL query the scheduling-dialog uses, so
   * the two surfaces report the same conflicts. Bails (clears the map)
   * when there's no time window or no non-organiser attendees.
   *
   * NOTE: this is best-effort — Graph 403 / network failure leaves the
   * map empty rather than surfacing a misleading "everyone is free"
   * state. The Calendar Reachability service handles the auth-path
   * messaging elsewhere; here we silently fall through.
   */
  private readonly conflictProbeEffect = effect(() => {
    const attendees = this.service.attendees();
    const start = this.service.startTime();
    const end = this.service.endTime();
    if (!start || !end) {
      this.service.conflictsByAttendee.set(new Map());
      return;
    }
    const others = attendees.filter(a => !a.isOrganiser);
    if (others.length === 0) {
      this.service.conflictsByAttendee.set(new Map());
      return;
    }
    if (this.conflictProbeDebounce) clearTimeout(this.conflictProbeDebounce);
    const emails = others.map(a => a.email.toLowerCase());
    // Use naive datetime strings (no Z) — peopleAvailability accepts
    // them and applies the timezone via the `timezone` arg. See
    // `feedback_rdx_api_region_prefix` for the wider rule.
    // Load the FULL work day (7am–7pm of the meeting day) instead of
    // just the meeting window so the bmp-find-time-strip has data for
    // every busy band visible in its horizontal scroll, and the
    // "better times nearby" suggestions can scan beyond ±2h.
    const dayStart = new Date(start);
    dayStart.setHours(7, 0, 0, 0);
    const dayEnd = new Date(start);
    dayEnd.setHours(19, 0, 0, 0);
    const isoStart = this.naiveDatetime(dayStart);
    const isoEnd = this.naiveDatetime(dayEnd);
    const meetingStartMs = start.getTime();
    const meetingEndMs = end.getTime();
    const dayStartMs = dayStart.getTime();
    const tz = this.locationState.timezone();
    this.conflictProbeDebounce = setTimeout(async () => {
      try {
        const schedules = await this.scheduling.loadPeopleAvailability(
          emails,
          isoStart,
          isoEnd,
          tz,
          15,
        );
        const map = new Map<string, 'busy' | 'tentative' | 'oof'>();
        // Compute conflicts only against the meeting window even
        // though we loaded the full day. Slice the availabilityView
        // bitmap to the meeting window's 15-min slots so a busy slot
        // at 4pm doesn't poison the conflict map for a 9am meeting.
        const meetingStartSlot = Math.max(
          0,
          Math.floor((meetingStartMs - dayStartMs) / (15 * 60_000)),
        );
        const meetingEndSlot = Math.max(
          meetingStartSlot,
          Math.ceil((meetingEndMs - dayStartMs) / (15 * 60_000)),
        );
        for (const s of schedules) {
          const email = (s.email || '').toLowerCase();
          if (!email) continue;
          const view = (s.availabilityView || '').slice(meetingStartSlot, meetingEndSlot);
          let worst: 'busy' | 'tentative' | 'oof' | null = null;
          for (const ch of view) {
            if (ch === '2') { worst = 'busy'; break; }
            if (ch === '3') worst = 'oof';
            else if (ch === '1' && worst === null) worst = 'tentative';
          }
          if (worst) map.set(email, worst);
        }
        // `loadPeopleAvailability` returns `[]` on failure and records the
        // typed error on its own `error()` signal, which nothing here used to
        // read. So a Graph 403 or a dropped request walked THIS branch, not
        // the catch, with `schedules = []` — producing an empty conflict map
        // that is indistinguishable from "everybody is free".
        this.conflictProbeUnresolved.set(this.scheduling.error() !== null);
        this.service.conflictsByAttendee.set(map);
      } catch {
        // The old comment here said an empty map avoids "false 'everyone is
        // free' data". It IS that data: `conflictCount()` reads 0, the People
        // tab does not pulse, and the person books over three calendars they
        // were never shown. An unchecked calendar is not a free one.
        this.conflictProbeUnresolved.set(true);
        this.service.conflictsByAttendee.set(new Map());
      }
    }, 500);
  });

  /** Count of attendees with a 'busy' conflict — drives the summary
   *  warning above the CTA and the People tab pulse. */
  protected readonly conflictCount = computed(() => {
    let n = 0;
    for (const v of this.service.conflictsByAttendee().values()) {
      if (v === 'busy' || v === 'oof') n++;
    }
    return n;
  });

  /**
   * Compact one-line summary of the meeting state, rendered above the
   * footer CTA so the user can confirm what they're about to book
   * regardless of which tab they're looking at when they hit "Book
   * Meeting". Pieces are joined with " · " and omitted when blank, so
   * the line stays short for new bookings.
   */
  protected readonly formSummary = computed<string>(() => {
    const parts: string[] = [];
    const start = this.service.startTime();
    if (start) {
      const date = start.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      const time = start.toLocaleTimeString('en-GB', withClock({
        hour: '2-digit',
        minute: '2-digit',
      }));
      parts.push(`${date} · ${time}`);
    }
    const dur = this.service.duration();
    if (dur) {
      parts.push(dur < 60 ? `${dur} min` : `${Math.round(dur / 60)} hr`);
    }
    const rooms = this.service.rooms().length;
    if (rooms > 0) parts.push(`${rooms} room${rooms === 1 ? '' : 's'}`);
    const people = this.service.attendees().length;
    if (people > 0) parts.push(`${people} person${people === 1 ? '' : 's'}`);
    if (this.service.isOnlineMeeting()) parts.push('Teams');
    return parts.join(' · ');
  });

  /** Helper — naive datetime string ("YYYY-MM-DDTHH:MM:SS") in local
   *  time, no Z suffix. The peopleAvailability resolver applies the
   *  timezone separately so we MUST NOT send a UTC instant here. */
  private naiveDatetime(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  }

  // ── Smart Assist (intent classification) ──────────────────────
  //
  // Mirrors the scheduling-dialog's local classifier so the chip
  // appears instantly as the user types, with no backend round-trip.
  // The chip itself (IntentHintChipComponent) is the same atom the
  // scheduling-dialog uses — its `spaceTypeApplied` / `nameSearchRequested`
  // outputs are no-ops here (the panel doesn't have a room-filter or a
  // people search slot in the Meeting tab), but `durationSuggested` is
  // applied directly to the meeting end time.

  /**
   * Classify the meeting from subject + attendee count + duration —
   * returns `{ type, confidence }` matching the chip's expected inputs.
   * Null when no subject or attendee count is too low to classify.
   */
  protected readonly meetingIntent = computed<{ type: string; confidence: number } | null>(() => {
    const sub = this.service.subject().trim();
    if (!sub || sub.length < 3) return null;
    const attendeeCount = this.service.attendees().length;
    const dur = this.service.duration() ?? 30;
    return this.classifyMeetingIntentLocal(sub, attendeeCount, dur);
  });

  /** Apply the suggested duration by shifting endTime relative to startTime. */
  protected onIntentDurationSuggested(mins: number): void {
    const start = this.service.startTime();
    if (!start) return;
    const next = new Date(start);
    next.setMinutes(next.getMinutes() + mins);
    this.service.endTime.set(next);
  }

  /** Apply a suggested space type — switch to the Rooms tab so the
   *  user can act on the hint (the dropdown is open and ready). */
  protected onIntentSpaceTypeApplied(_spaceType: string): void {
    this.activeTab.set('rooms');
  }

  /** Routing only — switch to People tab so the user can add the
   *  extracted name. The chip surfaces the name; the picker is here. */
  protected onIntentNameSearchRequested(_name: string): void {
    this.activeTab.set('people');
  }

  /**
   * Local meeting-intent classifier — mirrors the backend regex tier
   * for instant feedback. Lifted from scheduling-dialog.ts so the two
   * surfaces stay in sync without a shared util (drift risk is low
   * — the rules are stable).
   */
  private classifyMeetingIntentLocal(
    subject: string,
    attendeeCount: number,
    duration: number,
  ): { type: string; confidence: number } {
    const s = (subject || '').toLowerCase().trim();
    if (s.includes('all hands') || s.includes('town hall') || attendeeCount > 30)
      return { type: 'all-hands', confidence: 0.9 };
    if (s.includes('board meeting') || s.includes('exec committee'))
      return { type: 'board-meeting', confidence: 0.9 };
    if (s.includes('interview') || s.includes('hiring') || s.includes('candidate'))
      return { type: 'interview', confidence: 0.9 };
    if ((s.includes('performance') && s.includes('review')) || s.includes('appraisal'))
      return { type: 'performance-review', confidence: 0.85 };
    if (s.includes('sprint planning') || s.includes('retrospective'))
      return { type: 'sprint-planning', confidence: 0.9 };
    if (s.includes('design review') || s.includes('ux review') || s.includes('whiteboard'))
      return { type: 'design-review', confidence: 0.85 };
    if (s.includes('training') || s.includes('onboarding'))
      return { type: 'training', confidence: 0.85 };
    if (s.includes('presentation') || s.includes('demo day'))
      return { type: 'presentation', confidence: 0.85 };
    if (s.includes('workshop') || s.includes('brainstorm'))
      return { type: 'workshop', confidence: 0.85 };
    if (s.includes('client') || s.includes('external') || s.includes('demo'))
      return { type: 'client-meeting', confidence: 0.8 };
    if (s.includes('happy hour') || s.includes('social') || s.includes('lunch'))
      return { type: 'social', confidence: 0.8 };
    if (s.includes('focus') || s.includes('deep work'))
      return { type: 'focus-time', confidence: 0.8 };
    if ((s.includes('standup') || s.includes('daily') || s.includes('scrum')) && duration <= 30)
      return { type: 'standup', confidence: 0.95 };
    if (s.includes('sync') || s.includes('weekly') || s.includes('team') || s.includes('catchup'))
      return { type: 'team-sync', confidence: attendeeCount >= 3 ? 0.75 : 0.6 };
    if (s.includes('catch up') || s.includes('catchup') || s.includes('chat') || s.includes('coffee'))
      return { type: '1:1', confidence: 0.75 };
    if (attendeeCount === 2 && duration <= 60) return { type: '1:1', confidence: 0.9 };
    if (attendeeCount === 0) return { type: 'general', confidence: 0.5 };
    return { type: 'general', confidence: 0.5 };
  }

  // ── Permission-aware chrome ─────────────────────────
  // Component-level computeds for the view/edit panel chrome (banner +
  // footer). Service exposes the raw signals (mode, canEdit, canCancel,
  // viewerIsOrganizer, ...); the panel composes them into UI-shaped state
  // here so the template stays declarative.

  /** True when the loaded meeting's end time is in the past — gates the
   *  "Join video call" footer button (no point joining a finished meeting)
   *  and informs the "view past meeting" footer variant. */
  protected readonly isPastMeeting = computed(() => {
    const end = this.service.endTime();
    return end ? end.getTime() < Date.now() : false;
  });

  // ── Flow recordings (any meeting state — past, present, future) ────
  //
  // Source of truth: `service.flowLinks` (FlowLinkSummary[] from
  // platform `flowsForMeeting` query) + `service.flowDetails` (rich
  // FlowMeetingContent from `flowMeetingContent`, lazily enriched per
  // link). No `isPastMeeting` gate — the desktop client lets users
  // record against any meeting (past memos, live calls, pre-recorded
  // briefs for future sessions).

  /** Helper — return the rich content (brief, topics, transcript) for a
   *  given FlowLink. Returns null while enrichment is in flight or the
   *  query failed; the FlowLink itself still carries the basic facts
   *  (duration, has-transcript, etc.) so the card always renders. */
  protected flowDetailFor(
    meetingId: string | null | undefined,
  ): FlowMeetingContent | null {
    if (!meetingId) return null;
    return this.service.flowDetails()[meetingId] ?? null;
  }

  /**
   * Per-flow inline reveal state. Maps `flowMeetingMeetingId` →
   * which sub-section (transcript / insights) is currently expanded
   * in the card. Multiple cards (multiple recordings on one meeting)
   * each get their own expansion state.
   *
   * Stored as a plain object so the signal change is shallow + cheap;
   * the template binds via `isFlowSectionOpen(meetingId, section)`.
   */
  private readonly _flowRevealState = signal<
    Record<string, Partial<Record<FlowRevealSection, boolean>>>
  >({});

  // Expose enums + tunables to the template — Angular templates can't
  // read module imports or static class members directly, only
  // component-instance properties.
  protected readonly FlowRevealSection = FlowRevealSection;
  protected readonly FlowNoteVariant = FlowNoteVariant;
  protected readonly AVATAR_STACK_LIMIT = BookMeetingPanelComponent.AVATAR_STACK_LIMIT;

  /** Whether the named sub-section is open for this Flow card. */
  protected isFlowSectionOpen(
    meetingId: string | null | undefined,
    section: FlowRevealSection,
  ): boolean {
    if (!meetingId) return false;
    return !!this._flowRevealState()[meetingId]?.[section];
  }

  /** Toggle a Flow sub-section open/closed. Called from the tag buttons. */
  protected toggleFlowSection(
    meetingId: string | null | undefined,
    section: FlowRevealSection,
  ): void {
    if (!meetingId) return;
    this._flowRevealState.update(state => {
      const current = state[meetingId] ?? {};
      return {
        ...state,
        [meetingId]: { ...current, [section]: !current[section] },
      };
    });
  }

  // ── Quick Win 2: Notes pane ────────────────────────────
  //
  // The flowMeetingContent payload already carries `noteFullText` and
  // `noteEnhancedText` — surface them as a third reveal pane next to
  // Transcript and AI insights. When both variants exist, default to
  // enhanced with a small toggle to switch back to original.

  /** True when at least one note variant is non-empty after trim. */
  protected hasNotes(detail: FlowMeetingContent | null): boolean {
    if (!detail) return false;
    return !!(
      (detail.noteFullText && detail.noteFullText.trim()) ||
      (detail.noteEnhancedText && detail.noteEnhancedText.trim())
    );
  }

  /** Per-card preference for which note variant the pane shows.
   *  Defaults to ENHANCED (Gemini cleanup) when present, otherwise RAW. */
  private readonly _noteVariantState = signal<
    Record<string, FlowNoteVariant>
  >({});

  protected noteVariant(
    meetingId: string | null | undefined,
    detail: FlowMeetingContent | null,
  ): FlowNoteVariant {
    if (!meetingId) return FlowNoteVariant.ENHANCED;
    const explicit = this._noteVariantState()[meetingId];
    if (explicit) return explicit;
    // Default: enhanced when present, else raw.
    return detail?.noteEnhancedText?.trim()
      ? FlowNoteVariant.ENHANCED
      : FlowNoteVariant.RAW;
  }

  protected setNoteVariant(
    meetingId: string | null | undefined,
    variant: FlowNoteVariant,
  ): void {
    if (!meetingId) return;
    this._noteVariantState.update(state => ({
      ...state,
      [meetingId]: variant,
    }));
  }

  /** Resolve the active note text for the given card based on the
   *  variant preference. Falls back to the other variant when the
   *  preferred one is empty so the pane never renders blank. */
  protected activeNoteText(
    meetingId: string | null | undefined,
    detail: FlowMeetingContent | null,
  ): string {
    if (!detail) return '';
    const variant = this.noteVariant(meetingId, detail);
    if (variant === FlowNoteVariant.ENHANCED) {
      return (
        detail.noteEnhancedText?.trim() ||
        detail.noteFullText?.trim() ||
        ''
      );
    }
    return (
      detail.noteFullText?.trim() ||
      detail.noteEnhancedText?.trim() ||
      ''
    );
  }

  /** True when both raw and enhanced note variants exist — the toggle
   *  inside the pane only renders in that case. */
  protected hasBothNoteVariants(detail: FlowMeetingContent | null): boolean {
    if (!detail) return false;
    return !!(
      detail.noteFullText?.trim() && detail.noteEnhancedText?.trim()
    );
  }

  // ── Quick Win 3: Transcript search ─────────────────────

  private readonly _transcriptQueryState = signal<Record<string, string>>({});

  protected transcriptQuery(meetingId: string | null | undefined): string {
    if (!meetingId) return '';
    return this._transcriptQueryState()[meetingId] ?? '';
  }

  protected setTranscriptQuery(
    meetingId: string | null | undefined,
    query: string,
  ): void {
    if (!meetingId) return;
    this._transcriptQueryState.update(state => ({
      ...state,
      [meetingId]: query,
    }));
  }

  /** Filter transcript segments by the per-card query. Empty query
   *  returns all segments. Match is case-insensitive substring on the
   *  segment text. Counter shown alongside the search input is the
   *  return value's length. */
  protected filteredTranscript(
    detail: FlowMeetingContent | null,
    meetingId: string | null | undefined,
  ): FlowTranscriptSegment[] {
    if (!detail?.transcript) return [];
    const q = this.transcriptQuery(meetingId).trim().toLowerCase();
    if (!q) return detail.transcript;
    return detail.transcript.filter(seg =>
      seg.text?.toLowerCase().includes(q),
    );
  }

  /** Split a segment's text into a sequence of `{text, isMatch}` parts
   *  for the template's `<mark>` highlight pass. Used inside the
   *  filtered-transcript loop. Empty query returns a single non-match
   *  part covering the full text. */
  protected highlightedSegmentParts(
    text: string,
    meetingId: string | null | undefined,
  ): Array<{ text: string; isMatch: boolean }> {
    const q = this.transcriptQuery(meetingId).trim();
    if (!q) return [{ text, isMatch: false }];

    const parts: Array<{ text: string; isMatch: boolean }> = [];
    const lower = text.toLowerCase();
    const needle = q.toLowerCase();
    let cursor = 0;
    while (cursor < text.length) {
      const found = lower.indexOf(needle, cursor);
      if (found === -1) {
        parts.push({ text: text.slice(cursor), isMatch: false });
        break;
      }
      if (found > cursor) {
        parts.push({ text: text.slice(cursor, found), isMatch: false });
      }
      parts.push({
        text: text.slice(found, found + needle.length),
        isMatch: true,
      });
      cursor = found + needle.length;
    }
    return parts;
  }

  // ── Quick Win 4: Action item → clipboard (Linear-friendly) ────

  /** Whether a flattened insight block heading is recognised as a list
   *  of action items / TODOs / next steps. Drives the per-item Copy
   *  button render. The recognised headings live in the central
   *  `ACTIONABLE_INSIGHT_HEADINGS` set so they're easy to grow as the
   *  Gemini schema evolves. */
  protected isActionableHeading(heading: string): boolean {
    return ACTIONABLE_INSIGHT_HEADINGS.has(heading.toLowerCase().trim());
  }

  /** Per-item "Copied" chip swap state — flips for 2s after a successful
   *  copy, mirroring the share-link / passcode pattern. */
  private readonly _copiedActionState = signal<{
    flowId: string;
    index: number;
  } | null>(null);

  protected isActionCopied(
    flowId: string | null | undefined,
    index: number,
  ): boolean {
    if (!flowId) return false;
    const c = this._copiedActionState();
    return !!c && c.flowId === flowId && c.index === index;
  }

  /**
   * Build a Linear-friendly markdown task and write it to the clipboard.
   * The user pastes into Linear's quick-add (Q key) or any other
   * task-keyboard surface. Real Linear OAuth integration is a follow-up
   * bead — clipboard copy is the MVP.
   */
  protected async copyActionItem(
    flow: { flowMeetingMeetingId?: string | null },
    item: string,
    index: number,
  ): Promise<void> {
    const flowId = flow.flowMeetingMeetingId;
    if (!flowId) return;

    const subject = this.service.subject() || 'Untitled meeting';
    const start = this.service.startTime();
    const dateLabel = start
      ? start.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
    const sourceUrl = window.location.href;

    const lines: string[] = [item.trim(), ''];
    lines.push(`From: ${subject}${dateLabel ? ` · ${dateLabel}` : ''}`);
    lines.push(`Source: ${sourceUrl}`);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this._copiedActionState.set({ flowId, index });
      setTimeout(() => {
        const c = this._copiedActionState();
        if (c?.flowId === flowId && c.index === index) {
          this._copiedActionState.set(null);
        }
      }, BookMeetingPanelComponent.COPY_FEEDBACK_MS);
    } catch (err) {
      console.warn('[BookMeeting] copyActionItem clipboard failed:', err);
    }
  }

  // ── Quick Win 1: Speaker rename (frontend) ─────────────

  /** Which speaker label is currently in inline-edit mode (mutually
   *  exclusive across all flow cards on the panel). Null = none. */
  private readonly _speakerEditState = signal<{
    flowId: string;
    speakerId: string;
  } | null>(null);

  /** Per-edit-session typeahead query. Cleared when edit closes. */
  private readonly _speakerEditQuery = signal('');

  protected isSpeakerEditing(
    flowId: string | null | undefined,
    speakerId: string | null | undefined,
  ): boolean {
    if (!flowId || !speakerId) return false;
    const s = this._speakerEditState();
    return !!s && s.flowId === flowId && s.speakerId === speakerId;
  }

  /** True when the rename popover is open for ANY speaker on this Flow
   *  card. The popover renders once at the top of the transcript pane
   *  (not per-segment) so this drives the conditional render. */
  protected isAnySpeakerEditingForFlow(
    flowId: string | null | undefined,
  ): boolean {
    if (!flowId) return false;
    const s = this._speakerEditState();
    return !!s && s.flowId === flowId;
  }

  /** The speakerId currently being renamed on this Flow card, or null. */
  protected editingSpeakerIdFor(
    flowId: string | null | undefined,
  ): string | null {
    if (!flowId) return null;
    const s = this._speakerEditState();
    return s && s.flowId === flowId ? s.speakerId : null;
  }

  /** The currently-displayed label for the speaker being renamed —
   *  drives the popover title ("Renaming Speaker 2"). Reads from the
   *  first transcript segment matching the editing speakerId. */
  protected editingSpeakerLabelFor(
    flowId: string | null | undefined,
    detail: FlowMeetingContent | null,
  ): string {
    const editingSpeakerId = this.editingSpeakerIdFor(flowId);
    if (!editingSpeakerId || !detail) return '';
    const seg = detail.transcript.find(s => s.speakerId === editingSpeakerId);
    return seg?.speakerLabel ?? editingSpeakerId;
  }

  protected speakerEditQuery(): string {
    return this._speakerEditQuery();
  }

  protected setSpeakerEditQuery(q: string): void {
    this._speakerEditQuery.set(q);
  }

  /** Open the rename popover for one speaker on one Flow card. The
   *  initial query is seeded with the current label (renamed value
   *  wins) so the user can lightly edit rather than retype. */
  protected beginRenameSpeaker(
    flowId: string | null | undefined,
    speakerId: string | null | undefined,
    currentLabel: string | null | undefined,
  ): void {
    if (!flowId || !speakerId) return;
    this._speakerEditState.set({ flowId, speakerId });
    this._speakerEditQuery.set(currentLabel ?? '');
  }

  protected cancelRenameSpeaker(): void {
    this._speakerEditState.set(null);
    this._speakerEditQuery.set('');
  }

  /** Persist the rename via the backend. Optimistic update happens in
   *  the service. ESC / blur with empty value cancels. */
  protected async commitRenameSpeaker(
    flowId: string | null | undefined,
    speakerId: string | null | undefined,
    displayName: string,
  ): Promise<void> {
    if (!flowId || !speakerId) return;
    const trimmed = (displayName ?? '').trim();
    if (!trimmed) {
      this.cancelRenameSpeaker();
      return;
    }
    await this.service.setFlowSpeakerName(flowId, speakerId, trimmed);
    this.cancelRenameSpeaker();
  }

  /**
   * Two suggestion lists for the speaker rename popover:
   *
   *   - `attendees`  — meeting attendees first (most likely speakers).
   *   - `directory`  — broader employee directory (everyone in the
   *     tenant) for picking someone who joined via dial-in or wasn't
   *     on the original invite.
   *
   * Both lists are filtered by the same typeahead query and de-dupe
   * each other (an attendee won't appear in the directory section).
   * Capped at 5 each to keep the popover compact. Each entry carries
   * an `assignedUserId` + `assignedUserEmail` so picking it commits
   * a structured assignment (vs free-text), letting the backend
   * cluster recordings by user identity over time.
   */
  protected speakerSuggestions(): {
    attendees: SpeakerSuggestion[];
    directory: SpeakerSuggestion[];
  } {
    const q = this._speakerEditQuery().trim().toLowerCase();
    const matchesQuery = (text: string | null | undefined): boolean => {
      if (!q) return true;
      return !!text && text.toLowerCase().includes(q);
    };

    const attendeeSet = new Set<string>();
    const attendees: SpeakerSuggestion[] = [];
    for (const a of this.service.attendees()) {
      if (!matchesQuery(a.name) && !matchesQuery(a.email)) continue;
      const key = a.email?.trim().toLowerCase() || a.id;
      if (!key || attendeeSet.has(key)) continue;
      attendeeSet.add(key);
      attendees.push({
        id: a.id,
        name: a.name || a.email || 'Unknown',
        email: a.email,
        subtitle: a.jobTitle || a.department || a.email,
        avatar: a.picture,
        assignedUserId: a.id,
        assignedUserEmail: a.email,
      });
      if (attendees.length >= BookMeetingPanelComponent.SUGGESTION_LIMIT) break;
    }

    const directory: SpeakerSuggestion[] = [];
    for (const p of this.profileData.allPeople()) {
      if (!matchesQuery(p.name) && !matchesQuery(p.email)) continue;
      const key = p.email?.trim().toLowerCase() || p.userId;
      if (!key || attendeeSet.has(key)) continue;
      directory.push({
        id: p.userId,
        name: p.name,
        email: p.email,
        subtitle: p.email,
        avatar: p.avatar,
        assignedUserId: p.userId,
        assignedUserEmail: p.email,
      });
      if (directory.length >= BookMeetingPanelComponent.SUGGESTION_LIMIT) break;
    }

    return { attendees, directory };
  }

  /** Commit a rename from a typeahead pick — passes through the
   *  registered user's id + email so the backend can persist a
   *  structured assignment for future voice clustering. */
  protected async commitRenameSpeakerFromSuggestion(
    flowId: string | null | undefined,
    speakerId: string | null | undefined,
    suggestion: SpeakerSuggestion,
  ): Promise<void> {
    if (!flowId || !speakerId) return;
    await this.service.setFlowSpeakerName(
      flowId,
      speakerId,
      suggestion.name,
      {
        assignedUserId: suggestion.assignedUserId,
        assignedUserEmail: suggestion.assignedUserEmail,
      },
    );
    this.cancelRenameSpeaker();
  }

  /**
   * Parse the JSON-encoded detailed summary into a flat list of
   * `{ heading, items[] }` blocks for templated rendering. The shape
   * Gemini emits varies: top-level keys may map to string arrays
   * (action items, decisions, etc.), nested objects, or single strings.
   * The flattener picks "what's renderable as a bullet list" without
   * dropping anything informative. Returns empty array when there's
   * nothing structured (panel hides the AI insights pane in that case).
   */
  protected detailedSummaryBlocks(
    detail: FlowMeetingContent | null,
  ): Array<{ heading: string; items: string[] }> {
    if (!detail?.detailedSummaryJson) return [];
    let parsed: unknown;
    try {
      parsed = JSON.parse(detail.detailedSummaryJson);
    } catch {
      return [];
    }
    if (!parsed || typeof parsed !== 'object') return [];

    const blocks: Array<{ heading: string; items: string[] }> = [];
    for (const [key, raw] of Object.entries(parsed as Record<string, unknown>)) {
      const heading = humaniseKey(key);
      if (Array.isArray(raw)) {
        const items = raw
          .map(v => (typeof v === 'string' ? v : JSON.stringify(v)))
          .filter(Boolean);
        if (items.length > 0) blocks.push({ heading, items });
      } else if (typeof raw === 'string' && raw.trim()) {
        blocks.push({ heading, items: [raw] });
      } else if (raw && typeof raw === 'object') {
        const items = Object.entries(raw as Record<string, unknown>)
          .map(([k, v]) => `${humaniseKey(k)}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
          .filter(Boolean);
        if (items.length > 0) blocks.push({ heading, items });
      }
    }
    return blocks;
  }

  /**
   * Resolved named speakers for a Flow card header. Joins each speaker_map
   * entry against the meeting attendees + employee directory so we can
   * show an avatar in the card chrome (proof that the system knows who
   * was talking, not just "N speakers").
   *
   * Returns a stable shape ordered by transcript appearance — named
   * speakers come first (in the order they first speak in the recording),
   * unnamed speakers are dropped (the header counts them separately so
   * we can render "Nathan & Kiren · +1 unknown" elegantly).
   */
  protected namedSpeakers(
    detail: FlowMeetingContent | null,
  ): Array<{
    speakerId: string;
    name: string;
    email?: string | null;
    avatar?: string | null;
  }> {
    if (!detail?.speakerMap?.length || !detail?.transcript?.length) return [];

    // Build name lookup keyed on speakerId.
    const nameBySpeakerId = new Map<
      string,
      { name: string; email?: string | null; avatar?: string | null }
    >();
    for (const entry of detail.speakerMap) {
      // Resolve avatar by joining the assignment against the directory.
      // Cheap — allPeople() is in-memory and capped per location.
      let avatar: string | null | undefined = null;
      if (entry.assignedUserEmail) {
        const lcEmail = entry.assignedUserEmail.toLowerCase();
        const match = this.profileData
          .allPeople()
          .find(p => p.email?.toLowerCase() === lcEmail);
        avatar = match?.avatar ?? null;
      }
      nameBySpeakerId.set(entry.speakerId, {
        name: entry.displayName,
        email: entry.assignedUserEmail,
        avatar,
      });
    }

    // Walk the transcript in order — emit each named speaker the first
    // time they appear, skip duplicates and unnamed pseudonyms.
    const seen = new Set<string>();
    const result: Array<{
      speakerId: string;
      name: string;
      email?: string | null;
      avatar?: string | null;
    }> = [];
    for (const seg of detail.transcript) {
      if (!seg.speakerId) continue;
      if (seen.has(seg.speakerId)) continue;
      seen.add(seg.speakerId);
      const named = nameBySpeakerId.get(seg.speakerId);
      if (named) {
        result.push({ speakerId: seg.speakerId, ...named });
      }
    }
    return result;
  }

  /**
   * Total distinct speaker pseudonyms in the recording — derived from
   * the transcript rather than the FlowLink's `participantCount` because
   * the latter is whatever the desktop app stamped on attach (may not
   * match the transcript exactly after re-processing).
   */
  protected totalSpeakerCount(
    detail: FlowMeetingContent | null,
  ): number {
    if (!detail?.transcript?.length) return 0;
    const ids = new Set<string>();
    for (const seg of detail.transcript) {
      if (seg.speakerId) ids.add(seg.speakerId);
    }
    return ids.size;
  }

  /**
   * Header subtitle for the Flow card — replaces the bare "N speakers"
   * label with the actual names when we have them, falling back
   * gracefully:
   *
   *   - All speakers named:  "Nathan Thomas & Kiren Dosanjh-Dixon"
   *   - Mixed:               "Nathan Thomas · +1 unknown"
   *   - All unknown:         "1 speaker" / "3 speakers"  (legacy text)
   */
  protected speakersHeaderText(
    flow: { participantCount?: number | null },
    detail: FlowMeetingContent | null,
  ): string {
    const named = this.namedSpeakers(detail);
    const total = this.totalSpeakerCount(detail) || flow.participantCount || 0;

    if (named.length === 0) {
      if (total <= 0) return '';
      return `${total} ${total === 1 ? 'speaker' : 'speakers'}`;
    }

    // First names only when there are 3+ — keeps the header on one line.
    const firstName = (full: string): string =>
      full.split(/\s+/)[0] ?? full;

    let displayed: string;
    if (named.length === 1) {
      displayed = named[0].name;
    } else if (named.length === 2) {
      displayed = `${named[0].name} & ${named[1].name}`;
    } else {
      // 3+ named: first names joined.
      displayed = named.map(s => firstName(s.name)).join(', ');
    }

    const unknown = Math.max(0, total - named.length);
    if (unknown > 0) {
      displayed += ` · +${unknown} unknown`;
    }
    return displayed;
  }

  /** Format a transcript segment timestamp as `m:ss` for the speaker row.
   *  Delegates to the shared `formatSecondsMinSec` helper so this surface
   *  and the Flow `session-content` formatter can't drift. */
  protected formatSegmentTime(seconds: number | null | undefined): string {
    return formatSecondsMinSec(seconds);
  }

  /** Teams / Google Meet URL for the loaded meeting, or null. Surfaces in
   *  the footer when mode=view + viewer can't edit + meeting is online +
   *  in the future. */
  protected readonly joinUrl = computed(() => {
    if (!this.service.isOnlineMeeting()) return null;
    return this.service.loadedOnlineMeetingUrl();
  });

  /** Organiser display name for the banner attribution. Falls back to a
   *  friendly default so the banner copy never reads "...notify null". */
  protected readonly organizerName = computed(() => {
    const explicit = this.service.loadedOrganizerName();
    if (explicit && explicit.trim().length) return explicit;
    const orgAttendee = this.service.attendees().find(a => a.isOrganiser);
    return orgAttendee?.name?.trim() || orgAttendee?.email || 'this user';
  });

  /** "Admin view" amber banner — shown in view mode when the viewer can edit
   *  but isn't the organiser (e.g., user has BOOKING_UPDATE_ANY). */
  protected readonly showAdminViewBanner = computed(() =>
    this.service.mode() === 'view'
    && this.service.canEdit()
    && !this.service.viewerIsOrganizer(),
  );

  /** "Editing as admin" red banner — shown in edit mode when viewer is not
   *  the organiser. Reminds the editor that the change will notify the
   *  meeting's actual organiser. */
  protected readonly showAdminEditBanner = computed(() =>
    this.service.mode() === 'edit'
    && !this.service.viewerIsOrganizer(),
  );

  /** Grouped linked catering orders by spaceId for multi-room meetings.
   *  Returns an array of { roomName, orders[] } with deterministic ordering:
   *  primary room first, then additional rooms in their `service.rooms()`
   *  order, then "Unassigned" for orders with no spaceId. Single-room
   *  meetings collapse to a single group with the room name. */
  protected readonly groupedLinkedCateringOrders = computed(() => {
    const orders = this.service.linkedCateringOrders();
    const rooms = this.service.rooms();
    if (orders.length === 0) return [] as Array<{ roomName: string | null; orders: LinkedCateringOrder[] }>;

    // Single-room meeting → flat list, no grouping label.
    if (rooms.length <= 1) {
      return [{ roomName: null, orders }];
    }

    const byRoom = new Map<string, LinkedCateringOrder[]>();
    const unassigned: LinkedCateringOrder[] = [];
    for (const o of orders) {
      if (o.spaceId) {
        const existing = byRoom.get(o.spaceId) ?? [];
        existing.push(o);
        byRoom.set(o.spaceId, existing);
      } else {
        unassigned.push(o);
      }
    }

    const groups: Array<{ roomName: string | null; orders: LinkedCateringOrder[] }> = [];
    for (const r of rooms) {
      const list = byRoom.get(r.spaceId);
      if (list && list.length) groups.push({ roomName: r.name, orders: list });
    }
    if (unassigned.length) groups.push({ roomName: 'Unassigned', orders: unassigned });
    return groups;
  });

  /** Format a linked catering order's total, defaulting to GBP if the order
   *  payload didn't ship a currency (defensive — backend should always send
   *  one but the panel mustn't crash if it doesn't). */
  protected formatLinkedOrderTotal(order: LinkedCateringOrder): string {
    return formatCurrencyMinor(order.totalCostPence ?? 0, order.currency || 'GBP');
  }

  /** Format an ISO delivery time as "Wed 30 Apr · 10:00" — matches the date
   *  language used elsewhere in the panel (date-time section uses the same
   *  weekday + day + month format). */
  protected formatDeliveryTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short',
    });
    const time = d.toLocaleTimeString('en-GB', withClock({
      hour: '2-digit', minute: '2-digit', hour12: false,
    }));
    return `${date} · ${time}`;
  }

  /** Format the timestamp under the order row — "Vendor notified at 10:00". */
  protected formatTimeOnly(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString('en-GB', withClock({
      hour: '2-digit', minute: '2-digit', hour12: false,
    }));
  }


  // ── Resize ──────────────────────────────────────
  protected readonly panelWidth = signal(this.loadWidth());
  protected readonly panelInlineSize = computed(
    () => `min(100vw, ${this.panelWidth()}px)`,
  );
  protected readonly isResizing = signal(false);
  private _resizeCleanup: (() => void) | null = null;

  /** Toggles the inline multi-room picker below the rooms list. */
  protected readonly showRoomPicker = signal(false);

  /** Inline style that publishes the meeting-label colour as CSS custom
   *  properties on the panel root. Cards / chips inside the panel can
   *  read `--meeting-label-bg` / `--meeting-label-border` /
   *  `--meeting-label-text` to adopt the colour. The variables flip to
   *  light-mode tokens via @media (prefers-color-scheme: light) inside the
   *  panel stylesheet. */
  protected readonly meetingLabelStyle = computed(() => {
    const t = this.service.meetingLabelColor();
    if (!t) return '';
    return [
      `--meeting-label-bg: ${t.bgDark}`,
      `--meeting-label-border: ${t.borderDark}`,
      `--meeting-label-text: ${t.dark}`,
      `--meeting-label-bg-light: ${t.bgLight}`,
      `--meeting-label-border-light: ${t.borderLight}`,
      `--meeting-label-text-light: ${t.light}`,
    ].join(';');
  });

  /** Track active day tab per room (for multi-day catering) */
  private readonly _activeDays = signal<Record<string, number>>({});

  /** Per-room service selections (layout, equipment, AV) */
  private readonly _roomServices = signal<Record<string, RoomServices>>({});

  protected activeDayForRoom(spaceId: string): number {
    return this._activeDays()[spaceId] ?? 0;
  }

  protected setActiveDayForRoom(spaceId: string, dayIndex: number): void {
    this._activeDays.update(m => ({ ...m, [spaceId]: dayIndex }));
  }

  /** Find the first day that has catering orders (to offer "Copy from Day X") */
  protected sourceDayForCopy(spaceId: string, currentDay: number): number | null {
    const days = this.service.meetingDays();
    for (let i = 0; i < days.length; i++) {
      if (i === currentDay) continue;
      if (this.service.ordersForRoomAndDay(spaceId, i).length > 0) return i;
    }
    return null;
  }

  /** Get services for the currently active day tab (or all for single-day) */
  protected currentServicesForRoom(spaceId: string): ServiceRequestDraft[] {
    if (this.service.isMultiDay() && this.service.meetingDays().length > 1) {
      return this.service.servicesForRoomAndDay(spaceId, this.activeDayForRoom(spaceId));
    }
    return this.service.servicesForRoom(spaceId);
  }

  /**
   * Compact meta row parts for a room card — building, capacity sentinel,
   * floor — filtered to non-empty so the template can interleave dot
   * separators without ending up with hanging "Building · " when capacity
   * and floor are missing. The `__capacity__` sentinel triggers the
   * users-icon rendering in the template; everything else is a plain
   * string. Keeps the visual order stable: building → capacity → floor.
   */
  protected roomMetaParts(room: RoomSelection): string[] {
    // Prototype meta format: "{floor} · seats {n}". Building / location
    // is omitted (panel header carries that context). Rating star +
    // open-issues chips are appended separately by the template so
    // they stay tooltip-able. Capacity surfaces once hydrate has run,
    // even when the DB value is 0 / null — the seats chip falls back
    // to "—" so the user knows we tried. Fallback to "Meeting room"
    // only when there's no hydrated context at all.
    const parts: string[] = [];
    if (room.floorName) parts.push(room.floorName);
    // Hydrate sets `spaceType` whenever the query succeeds, so we
    // use that as the "we've tried" signal — capacity may still be
    // null/0 in the DB, but the chip should still render.
    if (typeof room.capacity === 'number' || room.spaceType) {
      parts.push('__capacity__');
    }
    return parts;
  }

  /** Display value for the seats chip — accepts 0 / null / undefined
   *  and emits a dash so the chip always reads cleanly. When a
   *  layout other than "auto" is selected and that layout supplies a
   *  capacity (either via the admin-authored SpaceLayout or via the
   *  built-in seatsMod defaults), we show the effective seat count
   *  for that layout. Surfaces the layout's capacity impact so the
   *  user can see at a glance that picking Theatre gets them 8 more
   *  seats than the room's base setup. */
  protected capacityLabel(room: RoomSelection): string {
    const base = typeof room.capacity === 'number' && room.capacity > 0 ? room.capacity : null;
    const effective = this.effectiveCapacity(room);
    if (effective == null && base == null) return '—';
    if (effective == null || effective === base) return String(base);
    const delta = base != null ? effective - base : 0;
    if (delta === 0) return String(effective);
    return `${effective} (${delta > 0 ? '+' : ''}${delta})`;
  }

  /** Effective seat count for the room given the currently-selected
   *  layout. Prefers the space-authored SpaceLayout.capacity when
   *  present; falls back to base + seatsMod from the built-in defaults
   *  (boardroom 0, theatre +8, classroom +4, U-shape -2, standing +12)
   *  so the legacy "Boardroom" layout the user sees on rooms without
   *  authored layouts still surfaces a sensible number. */
  private effectiveCapacity(room: RoomSelection): number | null {
    const base = typeof room.capacity === 'number' && room.capacity > 0 ? room.capacity : null;
    const layoutKey = room.layoutKey;
    if (!layoutKey || layoutKey === 'auto') return base;

    // Admin-authored layout match wins.
    const authored = room.layouts?.find(
      l => l.name?.toLowerCase().replace(/\s+/g, '') === layoutKey.toLowerCase().replace(/\s+/g, ''),
    );
    if (authored?.capacity != null && authored.capacity > 0) return authored.capacity;

    // Fall back to the built-in seatsMod defaults.
    const seatsMod = ({
      boardroom: 0,
      theatre: 8,
      classroom: 4,
      ushape: -2,
      standing: 12,
    } as Record<string, number>)[layoutKey] ?? 0;

    if (base == null) return null;
    return Math.max(2, base + seatsMod);
  }

  /** True when the selected layout shifts the seat count away from
   *  the room's base capacity — drives the chip's adjusted highlight. */
  protected isCapacityAdjusted(room: RoomSelection): boolean {
    const base = typeof room.capacity === 'number' && room.capacity > 0 ? room.capacity : null;
    const eff = this.effectiveCapacity(room);
    return base != null && eff != null && eff !== base;
  }

  /** Tooltip text for the seats chip — surfaces base capacity AND
   *  the layout-adjusted figure when the two differ. */
  protected capacityTooltip(room: RoomSelection): string {
    this.locale.locale();
    const base = typeof room.capacity === 'number' && room.capacity > 0 ? room.capacity : null;
    const eff = this.effectiveCapacity(room);
    if (base == null) return this.transloco.translate('viewer.book_meeting_panel.capacity.not_set');
    if (eff == null || eff === base) return this.transloco.translate('viewer.book_meeting_panel.capacity.base', { base });
    const layout = room.layoutKey ?? this.transloco.translate('viewer.book_meeting_panel.capacity.selected_layout');
    const delta = eff - base;
    const sign = delta > 0 ? '+' : '';
    return this.transloco.translate('viewer.book_meeting_panel.capacity.layout_adjusted', { base, layout, eff, delta: `${sign}${delta}` });
  }

  /** Setup / reset hint string for the room's currently-selected
   *  layout — returns null when the layout reserves no buffer time
   *  so the warning chip stays hidden for the common "as-is" case.
   *  Replaces the standalone hint that used to live inside the
   *  legacy im-room-services tile. */
  protected roomLayoutSetupHint(room: RoomSelection): string | null {
    const layoutKey = room.layoutKey;
    if (!layoutKey || !room.layouts?.length) return null;
    const match = room.layouts.find(
      l => l.name?.toLowerCase().replace(/\s+/g, '') === layoutKey.toLowerCase().replace(/\s+/g, ''),
    );
    if (!match) return null;
    const setup = match.setupMinutes ?? 0;
    const reset = match.resetMinutes ?? 0;
    if (setup === 0 && reset === 0) return null;
    const parts: string[] = [];
    if (setup > 0) parts.push(`${setup}m setup`);
    if (reset > 0) parts.push(`${reset}m reset`);
    return `Facilities will hold ${parts.join(' · ')} on the room schedule`;
  }

  // ── Room Services ───────────────────────────────

  /** Return the RoomServices state for a given room, or a default empty state. */
  protected roomServices(spaceId: string): RoomServices {
    return this._roomServices()[spaceId] ?? { layout: null, equipment: [], av: null };
  }

  /** Update room services and sync preMinutes/postMinutes when a layout is selected.
   *  Setup / reset minutes come from the space's own SpaceLayout (admin-authored)
   *  rather than the legacy global ROOM_LAYOUTS constants. */
  protected onRoomServicesChange(spaceId: string, services: RoomServices): void {
    this._roomServices.update(map => ({ ...map, [spaceId]: services }));

    const primary = this.service.primaryRoom();
    if (!primary || primary.spaceId !== spaceId) return;

    if (services.layout) {
      const layout = (primary.layouts ?? []).find(l => l._id === services.layout);
      if (layout) {
        this.service.preMinutes.set(layout.setupMinutes ?? 0);
        this.service.postMinutes.set(layout.resetMinutes ?? 0);
      }
    } else {
      this.service.preMinutes.set(0);
      this.service.postMinutes.set(0);
    }
  }

  /** Whether any attendee is joining remotely. */
  protected hasRemoteAttendees(): boolean {
    return this.service.attendees().some(a => a.mode === 'remote');
  }

  /** Whether any attendee is external. */
  protected hasExternalAttendees(): boolean {
    return this.service.attendees().some(a => a.external);
  }

  // ── Resize drag ──────────────────────────────────

  onResizeStart(event: MouseEvent): void {
    if (
      typeof matchMedia !== 'undefined' &&
      matchMedia('(max-width: 768px)').matches
    ) {
      return;
    }
    event.preventDefault();
    this.isResizing.set(true);

    const startX = event.clientX;
    const startWidth = this.panelWidth();

    this.zone.runOutsideAngular(() => {
      const onMove = (e: MouseEvent) => {
        const delta = startX - e.clientX;
        const newWidth = Math.min(
          BookMeetingPanelComponent.MAX_WIDTH,
          Math.max(BookMeetingPanelComponent.MIN_WIDTH, startWidth + delta),
        );
        this.panelWidth.set(newWidth);
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        this._resizeCleanup = null;
        this.zone.run(() => {
          this.isResizing.set(false);
          this.saveWidth(this.panelWidth());
        });
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      this._resizeCleanup = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
      };
    });
  }

  protected formattedCateringTotal(): string {
    return formatCurrencyMinor(this.service.totalCateringPence(), 'GBP');
  }

  async onSubmit(): Promise<void> {
    // ──────────────────────────────────────────────────────────────────
    // Phase 1 — drain the autosave pipeline so the draft we're about to
    // discard reflects the latest server-assigned id.
    //
    // Why: the autosave debounces user edits 750ms in the future. If
    // the user hits "Book meeting" during that window, the pending
    // timer would fire AFTER the booking lands and re-create the
    // already-deleted draft on the server. Cancel the timer, then wait
    // for any in-flight save to resolve so `_draftId` is the final
    // server ObjectId (not the temp `draft-X` placeholder that gets
    // swapped out internally).
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.autosaveInFlight) {
      try { await this.autosaveInFlight; } catch { /* swallow */ }
    }

    // Snapshot of the booking shape we're about to submit — used both
    // for the draft content-match fallback below AND for the synthetic
    // realtime event we push after success.
    const start = this.service.startTime();
    const primary = this.service.primaryRoom();
    const submittedTitle = this.service.subject().trim() || 'New meeting';
    const submittedStartIso = start?.toISOString() ?? '';

    await this.service.submitBooking();

    if (this.service.submitError()) return;

    // ──────────────────────────────────────────────────────────────────
    // Phase 2 — wipe the draft chip and any matching dock entry.
    this.dirty.set(false);

    // Primary path: panel knows the server id we wrote during autosave.
    if (this._draftId) {
      this.meetingDrafts.remove(this._draftId);
      this._draftId = null;
    }

    // Fallback: in pathological autosave-races we can end up with a
    // server-persisted draft whose id we never observed. Sweep up by
    // content (title + start time) so the dock doesn't leave an
    // orphan chip pointing at a meeting that's already booked.
    const orphan = this.meetingDrafts.drafts().find(d => {
      const payload = (d.payload ?? {}) as Record<string, unknown>;
      return d.title === submittedTitle
        && payload['startTimeIso'] === submittedStartIso;
    });
    if (orphan) this.meetingDrafts.remove(orphan.id);

    // ──────────────────────────────────────────────────────────────────
    // Phase 3 — repaint downstream surfaces that listen on the
    // realtime booking signal (schedule grid, home widgets). The
    // grid's effect reloads its dataset on every `lastBookingChange`
    // tick, regardless of whether the change came from the WS or
    // from a local action. Without this nudge the grid renders stale
    // until the next manual reload or until the WS finally pushes the
    // server-side event (which can take seconds — long enough for the
    // user to wonder if the booking even worked).
    // Multi-room repaint — push one synthetic BookingChanged per
    // unique room so the schedule grid's effect repaints every booked
    // row, not just the primary. The grid's reload is per-location;
    // dedupe by locationId to avoid firing the same reload N times
    // when all rooms share a building. For cross-site multi-room
    // (different locationIds), one event per location guarantees
    // every grid mount that's watching its building gets nudged.
    if (primary) {
      const seenLocations = new Set<string>();
      for (const room of this.service.rooms()) {
        if (seenLocations.has(room.locationId)) continue;
        seenLocations.add(room.locationId);
        this.realtime.lastBookingChange.set({
          bookingId: `local-${Date.now()}-${room.spaceId}`,
          changeType: 'CREATED',
          locationId: room.locationId,
          spaceId: room.spaceId,
          subject: submittedTitle,
          startTime: submittedStartIso || undefined,
          endTime: this.service.endTime()?.toISOString() ?? undefined,
        });
      }
    }
  }

  // ── View / Edit / Cancel actions ─────────────────────

  /** Flip the panel from view → edit. No-op when canEdit is false (the
   *  service guards against this anyway, but disabling the button up-front
   *  is the right UX). */
  protected onEditMeeting(): void {
    this.service.enterEditMode();
  }

  /** Discard pending edits and drop back to view mode. The save button
   *  morph reverts in lockstep via `service.mode()`. */
  protected onDiscardChanges(): void {
    this.service.discardChanges();
  }

  /** Persist edits via `submitEdit` — the service handles delta-only update,
   *  re-load, and snapshot rebase. Errors surface via `service.submitError`. */
  protected async onSaveChanges(): Promise<void> {
    const result = await this.service.submitEdit();
    if (result.success && result.bookingId) {
      // Same repaint contract as the CREATE path's synthetic nudge (see
      // afterSubmitSuccess): every grid/widget listening on the realtime
      // booking signal (legacy schedule, schedule redesign, catering sync)
      // repaints now instead of waiting for the server's WS echo. No
      // locationId on purpose — a wrong guess would let location-filtered
      // listeners skip the repaint; unscoped costs one debounced reload.
      // The later WS echo is idempotent for listeners (order-status no-ops
      // + prompt dedupe in catering sync; reload coalescing in the grids).
      this.realtime.lastBookingChange.set({
        bookingId: result.bookingId,
        changeType: 'updated',
        subject: this.service.subject() || undefined,
        startTime: this.service.startTime()?.toISOString(),
        endTime: this.service.endTime()?.toISOString(),
      });
    }
  }

  /** Open the loaded meeting's video call link in a new tab. Defensive: if
   *  the URL isn't present (which the disabled-state guard should prevent)
   *  fall back silently. */
  protected onJoinCall(): void {
    const url = this.joinUrl();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Copy a deep-link to the loaded meeting onto the clipboard. Mirrors the
   *  share button on the old meeting-detail-panel — uses `?meetingId=` so the
   *  recipient lands on the same panel via the URL deep-link effect. */
  protected readonly shareCopied = signal(false);
  protected async onShareMeeting(): Promise<void> {
    const id = this.service.loadedMeetingId() ?? this.service.loadedBookingId();
    if (!id) return;
    const url = `${window.location.origin}/#/home?meetingId=${encodeURIComponent(id)}`;
    try {
      await navigator.clipboard.writeText(url);
      this.shareCopied.set(true);
      setTimeout(() => this.shareCopied.set(false), 2000);
    } catch {
      /* clipboard blocked — silent failure is acceptable here */
    }
  }

  /** Copy the Teams Meeting ID. Used by the header chip + JOIN MEETING row.
   *  Decoupled from `shareCopied` so the user sees clear feedback per chip
   *  (the share link and the meeting ID copy independently). */
  protected readonly meetingIdCopied = signal(false);
  protected async onCopyMeetingId(value: string): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.meetingIdCopied.set(true);
      setTimeout(() => this.meetingIdCopied.set(false), 2000);
    } catch {
      /* clipboard blocked — silent failure */
    }
  }

  /** Same affordance for the passcode row in the JOIN MEETING card. */
  protected readonly passcodeCopied = signal(false);
  protected async onCopyPasscode(value: string): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      this.passcodeCopied.set(true);
      setTimeout(() => this.passcodeCopied.set(false), 2000);
    } catch {
      /* clipboard blocked — silent failure */
    }
  }

  /** Confirm + cancel the loaded meeting. Mirrors the destructive UX used
   *  elsewhere in the app (window.confirm) — replace with a custom dialog
   *  later if/when the design system gets one. Closes the panel on success. */
  protected async cancelMeeting(): Promise<void> {
    if (!this.service.canCancel()) return;
    const ok = window.confirm(
      'Cancel this meeting? This will notify all attendees.',
    );
    if (!ok) return;
    const bookingId = this.service.loadedBookingId();
    const subject = this.service.subject();
    const result = await this.service.cancelLoadedBooking();
    if (result.success) {
      // Realtime nudge (create-path parity): the cancelled block drops off
      // every listening grid immediately; the catering sync gets the same
      // `changeType: 'cancelled'` a WS push would carry, and the later
      // echo no-ops (cancelled orders are no longer in a live status).
      if (bookingId) {
        this.realtime.lastBookingChange.set({
          bookingId,
          changeType: 'cancelled',
          subject: subject || undefined,
        });
      }
      this.service.close();
    }
  }

  /** Retry the `bookingForEditing` query when the initial load failed. The
   *  panel surfaces `service.loadError()` and an action to retry; we re-use
   *  whichever id was already loaded. Falls back to closing the panel when
   *  there's nothing to retry against. */
  protected retryLoad(): void {
    const id = this.service.loadedBookingId() ?? this.service.loadedMeetingId();
    if (!id) {
      this.service.close();
      return;
    }
    void this.service.loadForView(id);
    // Ensure UserLibrary (categories + tags + templates) is loaded
    // so the classify-row's category picker + tag input have data
    // when the user opens them in view-mode. Idempotent — no-op on
    // subsequent calls.
    void this.userLibrary.loadAll();
  }

  /** Send the viewer's RSVP for the loaded meeting. Optimistic update is
   *  handled inside the service; on failure we surface the error message via
   *  the existing `submitError` channel so the chrome banner can render it. */
  protected async onRsvp(
    response: 'accepted' | 'tentative' | 'declined',
  ): Promise<void> {
    const result = await this.service.submitRsvp(response);
    if (!result.success && result.error) {
      this.service.submitError.set(result.error);
    }
  }

  /** Parsed Teams metadata + cleaned-up body extracted from the Graph mirror's
   *  HTML. Drives both the JOIN MEETING card (structured rows) and the
   *  AGENDA card (whatever HTML survives the Teams-template strip). Memoised
   *  by signal — recomputes only when description() or attendees() change.
   *
   *  We feed the attendee list in so the parser can convert `mailto:` links
   *  into mention chips with full identity attributes. */
  protected readonly parsedBody = computed(() => {
    const body = this.service.description() ?? '';
    const mentionAttendees: MentionAttendee[] = this.service.attendees().map(a => ({
      name: a.name ?? a.email ?? '',
      email: a.email,
      jobTitle: a.jobTitle,
      department: a.department,
      picture: a.picture,
    }));
    return parseTeamsBody(body, mentionAttendees);
  });

  /** SANITISED agenda body HTML. The meeting body originates in Outlook /
   *  Graph (third-party-controlled: any attendee's client authored it), so
   *  it must pass Angular's HTML sanitizer — never `bypassSecurityTrust…`.
   *  The sanitizer keeps the parser's structure (paragraphs, anchors,
   *  images, mention-chip classes) and strips scripts, event handlers and
   *  inline styles — the panel's tokens take over, which the dark-mode
   *  style scrubbing wanted anyway. */
  protected readonly agendaSafeHtml = computed(
    () =>
      this.sanitizer.sanitize(
        SecurityContext.HTML,
        this.parsedBody().cleanedHtml,
      ) ?? '',
  );

  /** True when the meeting carries any body content. The parser preserves the
   *  full body (agenda + Teams template), so the AGENDA card shows whenever
   *  there's anything to show. The JOIN MEETING card on top still surfaces
   *  the structured metadata for quick action access. */
  protected readonly hasAgenda = computed(() => {
    return this.parsedBody().cleanedHtml.trim().length > 0;
  });

  /** True when the loaded meeting has Teams join metadata to render. The JOIN
   *  MEETING card hides when there's no join URL AND no extracted Teams
   *  metadata (Meeting ID / Passcode), since rendering an empty card would
   *  be noise. */
  protected readonly hasJoinDetails = computed(() => {
    const parsed = this.parsedBody();
    return !!(
      this.service.loadedOnlineMeetingUrl() ||
      parsed.joinUrl ||
      parsed.meetingId ||
      parsed.passcode
    );
  });

  /** Effective join URL — prefer the canonical service signal (set during
   *  hydration from the Graph mirror), fall back to anything the body parser
   *  scraped out of Outlook's HTML template. */
  protected readonly effectiveJoinUrl = computed(() => {
    return this.service.loadedOnlineMeetingUrl() || this.parsedBody().joinUrl || null;
  });

  /** Compact date label for the view-mode meta strip — "Wed 29 Apr". Empty
   *  when no start time has hydrated yet. */
  protected readonly viewModeDateLabel = computed(() => {
    const t = this.service.startTime();
    if (!t) return '';
    return t.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  });

  /** Time-range label for the view-mode meta strip — "09:00 — 15:15". Empty
   *  when either bound is missing. */
  protected readonly viewModeTimeLabel = computed(() => {
    const start = this.service.startTime();
    const end = this.service.endTime();
    if (!start || !end) return '';
    const fmt = (d: Date) =>
      d.toLocaleTimeString('en-GB', withClock({ hour: '2-digit', minute: '2-digit' }));
    return `${fmt(start)} — ${fmt(end)}`;
  });

  /** Primary room name for the view-mode meta strip. Empty when no room is
   *  attached to the meeting. */
  protected readonly viewModeRoomLabel = computed(() => {
    const r = this.service.rooms()[0];
    return r?.name ?? '';
  });

  /** Eyebrow caps line for the view-mode header — "YOUR MEETING" when the
   *  viewer is the organiser, otherwise "ORGANISED BY {name}" so non-
   *  organisers see who owns it at a glance. */
  protected readonly viewModeEyebrow = computed(() => {
    this.locale.locale();
    const cat = this.service.loadedAutoCategory();
    const organiser = this.organizerName();
    const viewerIsOrg = this.service.viewerIsOrganizer();
    // Category-driven eyebrow takes priority — "1:1 WITH MASAYA",
    // "TRAVEL", "CUSTOMER", etc. Falls back to the organiser-attribution
    // string when the category is missing or `unknown`.
    const fromCategory = buildCategoryEyebrow(cat, organiser, viewerIsOrg);
    if (fromCategory) return fromCategory;
    if (viewerIsOrg) return this.transloco.translate('viewer.book_meeting_panel.eyebrow.your_meeting');
    return organiser
      ? this.transloco.translate('viewer.book_meeting_panel.eyebrow.organised_by', { name: organiser.toUpperCase() })
      : this.transloco.translate('viewer.book_meeting_panel.eyebrow.meeting');
  });

  /** Coloured pill rendered in the meta strip — at-a-glance category
   *  recognition without re-reading the eyebrow. Hidden for `unknown` /
   *  null category so the strip doesn't carry meaningless chrome. */
  protected readonly categoryChipLabel = computed(() =>
    categoryLabel(this.service.loadedAutoCategory()),
  );

  protected readonly categoryChipStyle = computed(() => {
    const hue = categoryHue(this.service.loadedAutoCategory());
    if (!hue) return null;
    // Hue-only — SCSS owns the theme-aware lightness / opacity so the
    // chip reads in both dark and light themes via `[data-theme="..."]`.
    return { '--cat-hue': hue } as Record<string, string>;
  });

  /** RSVP rendering — surfaces the GOING? card only when there's a viewer
   *  RSVP state to anchor against AND the viewer isn't the organiser
   *  (organisers don't RSVP to their own meetings). */
  protected readonly showGoingCard = computed(() => {
    if (!this.isView()) return false;
    if (this.service.viewerIsOrganizer()) return false;
    const status = this.service.viewerResponseStatus();
    return !!status && status !== 'organizer';
  });

  /** Map the Graph response status onto the GOING? status pill copy. The
   *  card always renders even when `notResponded` so the choice cards have
   *  somewhere to live; the pill text reflects the current state. */
  protected readonly goingPillLabel = computed(() => {
    this.locale.locale();
    switch (this.service.viewerResponseStatus()) {
      case 'accepted':            return this.transloco.translate('viewer.book_meeting_panel.going.yes');
      case 'tentativelyAccepted': return this.transloco.translate('viewer.book_meeting_panel.going.maybe');
      case 'declined':            return this.transloco.translate('viewer.book_meeting_panel.going.no');
      default:                    return this.transloco.translate('viewer.book_meeting_panel.going.not_responded');
    }
  });

  /** Tone class for the GOING? status pill — drives the colour treatment. */
  protected readonly goingPillTone = computed(() => {
    switch (this.service.viewerResponseStatus()) {
      case 'accepted': return 'ok';
      case 'tentativelyAccepted': return 'warn';
      case 'declined': return 'danger';
      default: return 'neutral';
    }
  });

  onEditCatering(draft: CateringOrderDraft, room: RoomSelection): void {
    // Hold the existing draft key — we'll remove it only when user confirms new version
    this.service.editingDraftKey.set(draft.idempotencyKey);

    // Re-select the vendor and init cart with the draft's items
    this.vendorService.selectVendor(draft.vendorId);
    this.cartService.initForVendor(
      draft.vendorId,
      draft.vendorName,
      draft.vendorType,
      draft.deliveryTime,
      draft.vendorLogo,
      // Restoring a saved draft: the currency it was priced in is ON the draft,
      // and without it the restored cart silently reverted to GBP.
      draft.currency,
    );

    // Re-add items to cart
    for (const item of draft.items) {
      this.cartService.addItemDirect(item);
    }

    // Open the menu browse for this room
    this.service.openCateringForRoom(room);
    this.service.navigateTo(BookMeetingView.MENU_BROWSE);
  }

  close(): void {
    // Create-mode close with unsaved changes → confirm dialog so the
    // user can pick "Save as draft" vs "Discard". View/edit-mode close
    // and clean (non-dirty) create close fall straight through.
    if (this.service.mode() === 'create' && this.dirty()) {
      this.showCloseConfirm.set(true);
      return;
    }
    this.actuallyClose();
  }

  /** Bypasses the confirm-on-close gate. Used by the dialog's "Discard
   *  draft" CTA, the minimise path (which already persisted the
   *  draft), and any future non-confirm close trigger. `reason` is
   *  threaded through to BookMeetingService.close so the BMP_CLOSED
   *  event records why the panel went away. */
  private actuallyClose(reason: 'submit' | 'minimise' | 'close-button' | 'esc' | 'route-change' = 'close-button'): void {
    // Reset the draft id so the next fresh open isn't anchored to the
    // chip the user just dismissed. onMinimiseDraft restores it just
    // before delegating to close(), so minimise → dock chip → restore
    // still flows through the same id correctly.
    this._draftId = null;
    this.dirty.set(false);
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    this.service.close(reason);
    // Restore focus to whatever element triggered the open() — keeps
    // keyboard users on a sensible anchor (the schedule cell, the room
    // tile, the home widget, etc.) so the next Tab doesn't dump them
    // at the top of the document. Guarded: the element may have been
    // detached from the DOM while the panel was open, in which case
    // focus stays where the browser put it (typically the body).
    const trigger = this.lastTrigger;
    this.lastTrigger = null;
    if (trigger && document.body.contains(trigger)) {
      queueMicrotask(() => trigger.focus());
    }
  }

  /** "Discard draft" CTA — drops the in-progress chip (if we have one
   *  on the server) and closes. */
  protected onConfirmDiscard(): void {
    this.showCloseConfirm.set(false);
    if (this._draftId) {
      this.meetingDrafts.remove(this._draftId);
    }
    this.actuallyClose();
  }

  /** "Save as draft" CTA — delegates to the existing minimise path
   *  which writes the draft and closes the panel. */
  protected onConfirmSaveAsDraft(): void {
    this.showCloseConfirm.set(false);
    this.onMinimiseDraft();
  }

  /** Dismiss the confirm dialog without closing the panel. */
  protected onConfirmCancel(): void {
    this.showCloseConfirm.set(false);
  }

  /**
   * Schedule V2 — push the current panel state into the localStorage-
   * backed drafts store, then close the panel. The dock chip the user
   * sees is a recall handle, not a persisted booking.
   *
   * Draft id strategy (deduped):
   *   1. If editing an existing meeting, use `loadedMeetingId` — re-
   *      minimising the same meeting upserts in place.
   *   2. Else if the restore effect already set `_draftId` (the user
   *      is minimising a draft they just restored), reuse it.
   *   3. Else look for an existing chip matching this draft's title +
   *      start time. The panel is mounted in multiple host pages (home,
   *      schedule, viewer); each instance has its own `_draftId`, so
   *      the content match is what prevents duplicate chips when the
   *      user minimises identical-looking drafts from different mounts.
   *   4. Otherwise mint a fresh `draft-${timestamp}`.
   */
  protected onMinimiseDraft(): void {
    const existingId = this.service.loadedMeetingId();
    const start = this.service.startTime();
    const end = this.service.endTime();
    const title = this.service.subject().trim() || 'New meeting';
    const startIso = start?.toISOString() ?? '';

    let id = existingId ?? this._draftId;
    if (!id) {
      // Content-match fallback — same title + start time → same chip.
      const match = this.meetingDrafts.drafts().find(d => {
        const payload = (d.payload ?? {}) as Record<string, unknown>;
        return d.title === title && payload['startTimeIso'] === startIso;
      });
      id = match?.id ?? `draft-${Date.now()}`;
    }
    this._draftId = id;

    const durationMin = start && end
      ? Math.max(15, Math.round((end.getTime() - start.getTime()) / 60_000))
      : (this.service.duration() ?? 30);
    const startMin = start
      ? start.getHours() * 60 + start.getMinutes()
      : 9 * 60;
    const dateLabel = start
      ? start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      : undefined;
    // upsert() returns a Promise<string> of the resolved server id;
    // fire-and-forget here because minimise closes the panel
    // immediately and the dock chip rebuilds from the drafts() signal
    // (which is updated synchronously via applyEntry before the
    // network call completes). Awaited variant: persistDraftSilently.
    void this.meetingDrafts.upsert({
      id,
      title,
      startMin,
      durationMin,
      dateLabel,
      payload: {
        subject: this.service.subject(),
        description: this.service.description(),
        startTimeIso: start?.toISOString() ?? null,
        endTimeIso: end?.toISOString() ?? null,
      },
    }).then(resolvedId => {
      // If the panel is still mounted on the next mount (the same
      // BookMeetingService instance), surface the resolved id so a
      // future re-open doesn't mint a duplicate. Cheap; the field is
      // overwritten on the next open() anyway.
      this._draftId = resolvedId;
    });
    this.analytics.capture(ANALYTICS_EVENTS.BMP_DRAFT_SAVED, {
      trigger: 'manual',
      draft_id: id,
    });
    // Minimise has already persisted the draft — bypass the confirm
    // gate so the user isn't asked the question they just answered.
    this.actuallyClose('minimise');
  }

  /** Locally-generated draft id, used when minimising a brand-new draft
   *  (no loadedMeetingId yet). Kept across minimise/restore cycles on
   *  the same panel instance so the same chip updates in place. Reset
   *  to null when the panel closes via the × button (not minimise) so
   *  the next fresh open mints a new draft. */
  private _draftId: string | null = null;

  // ─── Focus management ─────────────────────────────────────────────
  //
  // Restore focus to whatever element triggered the open() so keyboard
  // users land back where they started after closing the panel. The
  // current focus is captured on the open→visible transition; restored
  // on close. Falls back to no-op when the trigger isn't focusable
  // anymore (e.g. the schedule grid was rebuilt while the panel was
  // open).
  private lastTrigger: HTMLElement | null = null;

  /** Effect — fires on `service.visible()` going false → true and runs
   *  one frame later (queueMicrotask) so the panel chrome is mounted
   *  before we try to focus the title input. Captures the previously-
   *  focused element so close() can restore. */
  private readonly focusOnOpenEffect = effect(() => {
    const visible = this.service.visible();
    untracked(() => {
      if (!visible) return;
      // Capture the launcher BEFORE we move focus. document.activeElement
      // can be null (no focus) — that's fine, we just won't restore.
      const launcher = (document.activeElement as HTMLElement | null) ?? null;
      // Skip storing if the launcher lives inside the panel itself
      // (defensive — happens if a fresh open() fires while the panel
      // is already focused, which would otherwise overwrite the real
      // trigger from the previous cycle).
      if (launcher && !this.el.nativeElement.contains(launcher)) {
        this.lastTrigger = launcher;
      }
      queueMicrotask(() => this.focusTitleInput());
    });
  });

  /** Move focus to the title input, falling back to the first focusable
   *  child of the panel root. No-op in view mode (no title input renders)
   *  or when the panel is in a non-form view. */
  private focusTitleInput(): void {
    if (!this.service.visible()) return;
    const root = this.el.nativeElement as HTMLElement;
    const title = root.querySelector<HTMLElement>('#bmp-title-input');
    if (title) { title.focus(); return; }
    // Fallback — first focusable element inside the panel root.
    const first = root.querySelector<HTMLElement>(
      'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
  }

  // ─── Keyboard shortcuts ───────────────────────────────────────────
  //
  // Listens at the document level so the user can fire shortcuts from
  // anywhere inside the panel — including inputs (Cmd+Enter submits
  // even with the cursor in the title field). All handlers bail unless
  // the panel is visible. Cross-platform via metaKey || ctrlKey.
  //
  // Esc is the one exception — most popovers inside the panel handle
  // their own Esc (the category picker, the layout picker, etc. each
  // listen via CDK overlay). When the confirm-on-close dialog is
  // showing, the dialog atom handles Esc too. We only kick the close
  // flow when no popover is captured the keystroke first; the simplest
  // proxy is `showCloseConfirm() === false`, since the popovers
  // themselves stopPropagation when they're open.

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.service.visible()) return;
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key;

    // Cmd/Ctrl + Enter — submit when allowed.
    if (mod && !event.shiftKey && key === 'Enter') {
      if (this.service.canSubmit() && !this.service.isSubmitting()) {
        event.preventDefault();
        void this.onSubmit();
      }
      return;
    }

    // Cmd/Ctrl + S — force save as draft (mirror the minimise path).
    if (mod && !event.shiftKey && (key === 's' || key === 'S')) {
      event.preventDefault();
      this.onMinimiseDraft();
      return;
    }

    // Cmd/Ctrl + . — minimise to dock. macOS minimise muscle memory.
    if (mod && !event.shiftKey && key === '.') {
      event.preventDefault();
      this.onMinimiseDraft();
      return;
    }

    // Cmd/Ctrl + Shift + D — open drafts library.
    if (mod && event.shiftKey && (key === 'd' || key === 'D')) {
      event.preventDefault();
      this.meetingDrafts.openLibrary();
      return;
    }

    // Cmd/Ctrl + K — jump focus to the title input.
    if (mod && !event.shiftKey && (key === 'k' || key === 'K')) {
      event.preventDefault();
      this.focusTitleInput();
      return;
    }

    // Esc — close (with confirm if dirty). Skip when the confirm dialog
    // is already showing (paper-dialog handles its own Esc). Popovers that
    // are open inside the panel typically stopPropagation, so we won't
    // see those events here.
    if (key === 'Escape' && !this.showCloseConfirm()) {
      // Let inputs cancel their composition before we close.
      event.preventDefault();
      this.close();
      return;
    }
  }

  ngOnDestroy(): void {
    this._resizeCleanup?.();
    // Tear down the scroll-spy listener (and any pending RAF) so we
    // don't leak frame callbacks after the panel closes.
    const body = this.scrollContainer();
    if (body && this.scrollListener) {
      body.removeEventListener('scroll', this.scrollListener);
    }
    if (this.scrollSpyRaf !== null) {
      cancelAnimationFrame(this.scrollSpyRaf);
      this.scrollSpyRaf = null;
    }
    this.scrollListener = null;
    if (this.savedTickInterval) {
      clearInterval(this.savedTickInterval);
      this.savedTickInterval = null;
    }
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  private loadWidth(): number {
    try {
      const stored = localStorage.getItem(BookMeetingPanelComponent.STORAGE_KEY);
      if (stored) {
        const w = parseInt(stored, 10);
        if (w >= BookMeetingPanelComponent.MIN_WIDTH && w <= BookMeetingPanelComponent.MAX_WIDTH) {
          // If the user had a narrow legacy width but V2 is now on, bump
          // up to the v2 default so the right rail has room to render.
          if (this.scheduleV2Enabled && w < 760) {
            return BookMeetingPanelComponent.V2_DEFAULT_WIDTH;
          }
          return w;
        }
      }
    } catch { /* ignore */ }
    return this.scheduleV2Enabled
      ? BookMeetingPanelComponent.V2_DEFAULT_WIDTH
      : BookMeetingPanelComponent.DEFAULT_WIDTH;
  }

  private saveWidth(w: number): void {
    try {
      localStorage.setItem(BookMeetingPanelComponent.STORAGE_KEY, String(w));
    } catch { /* ignore */ }
  }

  /**
   * Render a Flow recording's duration as a compact "1h 12m" / "47m" /
   * "30s" string. Used by the Flow card title in the meeting panel. Falls
   * back to "Recording" when the duration is missing — the badge stays
   * meaningful even without metadata.
   */
  protected formatFlowDuration(seconds: number | null | undefined): string {
    if (seconds == null || seconds <= 0) {
      this.locale.locale();
      return this.transloco.translate('viewer.book_meeting_panel.flow.recording');
    }
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const totalMinutes = Math.round(seconds / 60);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
  }

  /**
   * The link that opens the EXISTING recording. Since rdx reads
   * FlowSessions (#994), `flowMeetingMeetingId` is a FlowSession id, which
   * the desktop app's `smartmeet://recording?id=` cannot open: so it opens
   * the web reader, `/flow/sessions/:id` (the app is hash-routed).
   *
   * Falls back to start-meeting only when the FlowLink has no
   * `flowMeetingMeetingId` (legacy attaches written before this field
   * was added), so the button keeps working even on stale data.
   */
  protected flowOpenUrl(flow: { flowMeetingMeetingId?: string | null; flowPlanId?: string | null }): string {
    if (flow.flowMeetingMeetingId) {
      // flow-session-build-2: straight to the session page's Recording tab when the API names the session.
      return `#${flowRecordingPath(flow.flowMeetingMeetingId, flow.flowPlanId ?? null)}`;
    }

    // Legacy fallback — re-launch the recorder against the same calendar
    // event. Won't take the user to the existing recording, but at least
    // the deep link still does *something* useful.
    const meetingId = this.service.loadedMeetingId();
    const subject = this.service.subject();
    const params = new URLSearchParams();
    params.set('title', subject || 'Untitled');
    if (meetingId) params.set('eventId', meetingId);
    if (subject) params.set('subject', subject);
    return `smartmeet://start-meeting?${params.toString()}`;
  }

  // ── Flow Projects (chip cluster + inline picker) ───────

  /** Whether the project picker popover is open. Single instance per
   *  meeting panel; click outside / ESC / cancel closes. */
  protected readonly projectPickerOpen = signal(false);

  /** Search query inside the picker. Cleared when the popover closes. */
  protected readonly projectPickerQuery = signal('');

  /** "Create new project" inline form is open. */
  protected readonly projectPickerCreating = signal(false);
  protected readonly projectPickerNewName = signal('');

  /** Filtered active projects for the picker list. */
  protected readonly projectPickerSuggestions = computed(() => {
    const q = this.projectPickerQuery().trim().toLowerCase();
    const all = this.flowProjects.activeProjects();
    if (!q) return all;
    return all.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  });

  /** True when this project already includes the loaded meeting —
   *  drives the picker's checkbox state + the toggle action. */
  protected isProjectAttached(projectId: string): boolean {
    return this.service
      .meetingProjects()
      .some(p => p._id === projectId);
  }

  /** Series info for the loaded meeting — null when one-off, populated
   *  by `loadMeetingSeriesInfo` after the picker opens. Drives the
   *  Phase 3 "include all N instances" opt-in. */
  protected readonly meetingSeriesInfo = signal<{
    seriesKey?: string | null;
    totalCount: number;
    seriesSubject?: string | null;
  } | null>(null);

  /** Whether the user has opted into series back-fill for the next
   *  add (per-picker-open; resets on close). */
  protected readonly includeSeriesOptIn = signal(false);

  protected async openProjectPicker(): Promise<void> {
    // Lazy-load the viewer's full project list the first time the
    // picker opens. Subsequent opens hit the cached signal.
    await this.flowProjects.load();
    this.projectPickerOpen.set(true);
    this.projectPickerQuery.set('');
    this.projectPickerCreating.set(false);
    this.projectPickerNewName.set('');
    this.includeSeriesOptIn.set(false);
    this.meetingSeriesInfo.set(null);

    // Fetch series info for the loaded meeting. Cheap query; surfaces
    // the recurring-series opt-in inline when the meeting has siblings.
    const meetingId = this.service.loadedCalendarMeetingId();
    if (meetingId) {
      const info = await this.flowProjects.fetchMeetingSeriesInfo(meetingId);
      this.meetingSeriesInfo.set(info);
    }
  }

  protected closeProjectPicker(): void {
    this.projectPickerOpen.set(false);
    this.projectPickerQuery.set('');
    this.projectPickerCreating.set(false);
    this.projectPickerNewName.set('');
    this.includeSeriesOptIn.set(false);
    this.meetingSeriesInfo.set(null);
  }

  /** True when the loaded meeting belongs to a series with at least one
   *  sibling — gates the picker's series opt-in row. */
  protected readonly hasMeetingSeries = computed(() => {
    const info = this.meetingSeriesInfo();
    return !!info?.seriesKey && info.totalCount > 1;
  });

  /** Click on an existing project chip — opens the dedicated project
   *  detail panel via the shell state. The picker stays available via
   *  the "+ Project" affordance for adding/removing in bulk. */
  protected openProjectDetail(projectId: string): void {
    this.shellState.openProjectDetail(projectId);
  }

  /** Toggle a project on/off the loaded meeting. Optimistic update via
   *  the service. When `includeSeriesOptIn` is true AND the meeting
   *  has a series, the add path also opts the project into all
   *  sibling occurrences. Routes to the OVERLAY save path when the
   *  viewer doesn't own the meeting — that path writes only to the
   *  viewer's UCM record (private), never to the parent FlowProject's
   *  meetingIds[]. The canonical organiser flow stays unchanged. */
  protected async toggleProjectAttachment(projectId: string): Promise<void> {
    const isAttached = this.isProjectAttached(projectId);
    // Permission split — view-mode viewers who don't own the meeting
    // get the overlay-only path. canEdit() is true for the organiser
    // OR admins with BOOKING_UPDATE_ANY.
    if (!this.service.canEdit()) {
      if (isAttached) {
        await this.service.detachProjectFromOverlay(projectId);
      } else {
        await this.service.attachProjectToOverlay(projectId);
      }
      return;
    }
    if (isAttached) {
      await this.service.removeFromProject(projectId);
    } else {
      const includeSeries = this.includeSeriesOptIn() && this.hasMeetingSeries();
      await this.service.addToProject(projectId, { includeSeries });
    }
  }

  /** Submit the inline "create new project" form. On success, the new
   *  project is created AND immediately attached to the loaded meeting,
   *  then the form collapses. Routes through the overlay path for
   *  viewers without edit permission (same split as toggleProjectAttachment). */
  protected async commitNewProject(): Promise<void> {
    const name = this.projectPickerNewName().trim();
    if (!name) return;
    const created = await this.flowProjects.createProject({ name });
    if (!created) return;
    if (!this.service.canEdit()) {
      await this.service.attachProjectToOverlay(created._id);
    } else {
      const includeSeries = this.includeSeriesOptIn() && this.hasMeetingSeries();
      await this.service.addToProject(created._id, { includeSeries });
    }
    this.projectPickerCreating.set(false);
    this.projectPickerNewName.set('');
  }

  /** Convenience for the template's continuity surface — returns the
   *  per-card continuity payload (recurring series + project siblings)
   *  or null when the recording has nothing to show. */
  protected continuityFor(
    meetingId: string | null | undefined,
  ): import('../../../../core/services/book-meeting.service').FlowRecordingContinuity | null {
    if (!meetingId) return null;
    return this.service.flowContinuity()[meetingId] ?? null;
  }

  /** Format a sibling occurrence's start time for the continuity row.
   *  "Mon 5 May, 09:00" — short enough for a single line, day-of-week
   *  for quick scanability. */
  protected formatOccurrenceTime(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', withClock({
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }));
  }

  // ── Flow card inline "Add to project" dropdown ─────────
  //
  // Granola-style quick-add — sits on each Flow card so users can
  // tag without scrolling up to the meeting panel header. Simpler
  // than the full picker: just search + list of unattached projects,
  // click to add optimistically. No "create new" inline (full
  // creation lives in the header picker) and no series opt-in (same).

  /** Which Flow card's inline dropdown is open (mutually exclusive
   *  across all Flow cards on the panel). null = closed. */
  private readonly _flowCardPickerFlowId = signal<string | null>(null);

  /** Per-open search query. Cleared when the dropdown closes. */
  private readonly _flowCardPickerQuery = signal('');

  protected isFlowCardPickerOpen(flowId: string | null | undefined): boolean {
    if (!flowId) return false;
    return this._flowCardPickerFlowId() === flowId;
  }

  protected flowCardPickerQuery(): string {
    return this._flowCardPickerQuery();
  }

  protected setFlowCardPickerQuery(q: string): void {
    this._flowCardPickerQuery.set(q);
  }

  protected async openFlowCardPicker(flowId: string | null | undefined): Promise<void> {
    if (!flowId) return;
    // Lazy-load the viewer's full project list the first time.
    await this.flowProjects.load();
    this._flowCardPickerFlowId.set(flowId);
    this._flowCardPickerQuery.set('');
  }

  protected closeFlowCardPicker(): void {
    this._flowCardPickerFlowId.set(null);
    this._flowCardPickerQuery.set('');
    this.flowCardPickerCreating.set(false);
    this.flowCardPickerNewName.set('');
  }

  /** Filtered list of projects NOT already attached to the loaded
   *  meeting — drives the inline dropdown. Empty when every project
   *  is already attached (the dropdown then surfaces an explicit
   *  "all projects added" hint rather than an empty list). */
  protected readonly flowCardPickerSuggestions = computed(() => {
    const q = this._flowCardPickerQuery().trim().toLowerCase();
    const attached = new Set(this.service.meetingProjects().map(p => p._id));
    const candidates = this.flowProjects.activeProjects().filter(
      p => !attached.has(p._id),
    );
    if (!q) return candidates;
    return candidates.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  });

  /** Total active projects — when this is 0 the dropdown surfaces a
   *  "create your first project" CTA that opens the header picker. */
  protected readonly flowCardPickerHasAnyProjects = computed(
    () => this.flowProjects.activeProjects().length > 0,
  );

  /** Project ID that was just added via this dropdown — drives the
   *  inline "✓ Added to <name>" affordance for ~1.5s before the
   *  dropdown auto-closes. Mirrors the service's recentlyAddedProjectId
   *  but scoped per-dropdown so other surfaces (chip cluster pulse) can
   *  read the canonical service signal independently. */
  protected readonly flowCardJustAddedId = signal<string | null>(null);
  private _flowCardJustAddedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Resolve a project's display name for the success affordance. */
  protected flowCardJustAddedName(): string | null {
    const id = this.flowCardJustAddedId();
    if (!id) return null;
    const proj = this.flowProjects.projects().find(p => p._id === id);
    return proj?.name ?? null;
  }

  /** Click on a project in the dropdown → tag the loaded meeting
   *  into it, optimistic. Shows a "✓ Added to <name>" success state
   *  inside the dropdown for ~1.5s before auto-closing. */
  protected async commitFlowCardPickerAdd(projectId: string): Promise<void> {
    const ok = await this.service.addToProject(projectId);
    if (!ok) return;
    this.showFlowCardJustAdded(projectId);
  }

  /** Common helper — set the in-dropdown success state and schedule
   *  auto-close. Used by both the picker-list add path and the inline
   *  create-and-attach path so both surfaces share the same affordance. */
  private showFlowCardJustAdded(projectId: string): void {
    if (this._flowCardJustAddedTimer) {
      clearTimeout(this._flowCardJustAddedTimer);
      this._flowCardJustAddedTimer = null;
    }
    this.flowCardJustAddedId.set(projectId);
    this._flowCardJustAddedTimer = setTimeout(() => {
      this.flowCardJustAddedId.set(null);
      this._flowCardJustAddedTimer = null;
      this.closeFlowCardPicker();
    }, 1500);
  }

  // ── Inline "create new project" flow inside the Flow card ───────
  //
  // Granola pattern — when the user has no projects (or just wants
  // to make a new one), they can create + attach in a single step
  // without leaving the Flow card. Mirrors the header picker's
  // create-form pattern but stays within the dropdown.

  protected readonly flowCardPickerCreating = signal(false);
  protected readonly flowCardPickerNewName = signal('');

  /** Open the inline create form. Seeds the name with the current
   *  search query so users who typed a non-existent name then click
   *  "Create" don't have to retype. */
  protected beginFlowCardCreate(): void {
    this.flowCardPickerNewName.set(this.flowCardPickerQuery());
    this.flowCardPickerCreating.set(true);
  }

  protected cancelFlowCardCreate(): void {
    this.flowCardPickerCreating.set(false);
    this.flowCardPickerNewName.set('');
  }

  /** Submit the inline create form — creates the project AND attaches
   *  it to the loaded meeting in one shot. Surfaces the same "✓ Added"
   *  success state as the picker-list path before auto-close. */
  protected async commitFlowCardCreate(): Promise<void> {
    const name = this.flowCardPickerNewName().trim();
    if (!name) return;
    const created = await this.flowProjects.createProject({ name });
    if (!created) {
      this.cancelFlowCardCreate();
      return;
    }
    const ok = await this.service.addToProject(created._id);
    this.flowCardPickerCreating.set(false);
    this.flowCardPickerNewName.set('');
    if (ok) {
      this.showFlowCardJustAdded(created._id);
    } else {
      this.closeFlowCardPicker();
    }
  }

  // ── Flow card "Add to Workspace" dropdown ──────────────
  //
  // Same pattern as the project picker: one dropdown per Flow card,
  // mutually exclusive. Picks a Knowledge workspace → calls the
  // addFlowToKnowledge mutation → shows a brief success affordance.
  //
  // CURRENTLY HIDDEN — "Add to project" is the canonical organising
  // action. When a project has a linked Knowledge workspace, the
  // backend's project-aware findOrCreateFlowSessionsFolder routes
  // any later recording into the right project folder transparently.
  // The wiring + state below is preserved (rather than deleted) so
  // re-enabling the chip is a one-line flip — set the flag below to
  // true. No other changes needed.
  protected readonly addToWorkspaceChipEnabled = signal(false);

  private readonly _flowWorkspacePickerFlowId = signal<string | null>(null);

  protected isFlowWorkspacePickerOpen(flowId: string | null | undefined): boolean {
    if (!flowId) return false;
    return this._flowWorkspacePickerFlowId() === flowId;
  }

  protected async openFlowWorkspacePicker(flowId: string | null | undefined): Promise<void> {
    if (!flowId) return;
    this.closeFlowCardPicker();
    await this.workspaceState.loadWorkspaces();
    this._flowWorkspacePickerFlowId.set(flowId);
  }

  protected closeFlowWorkspacePicker(): void {
    this._flowWorkspacePickerFlowId.set(null);
  }

  protected readonly flowWorkspacePickerSuggestions = computed(
    () => this.workspaceState.workspaces(),
  );

  protected readonly flowWorkspaceJustAddedId = signal<string | null>(null);
  private _flowWorkspaceJustAddedTimer: ReturnType<typeof setTimeout> | null = null;

  protected flowWorkspaceJustAddedName(): string | null {
    const id = this.flowWorkspaceJustAddedId();
    if (!id) return null;
    const ws = this.workspaceState.workspaces().find(w => w._id === id);
    return ws?.name ?? null;
  }

  protected async commitFlowWorkspaceAdd(workspaceId: string): Promise<void> {
    const flowId = this._flowWorkspacePickerFlowId();
    if (!flowId) return;
    const result = await this.service.addFlowToKnowledge(workspaceId, flowId);
    if (!result?.success) return;
    if (this._flowWorkspaceJustAddedTimer) {
      clearTimeout(this._flowWorkspaceJustAddedTimer);
      this._flowWorkspaceJustAddedTimer = null;
    }
    this.flowWorkspaceJustAddedId.set(workspaceId);
    this._flowWorkspaceJustAddedTimer = setTimeout(() => {
      this.flowWorkspaceJustAddedId.set(null);
      this._flowWorkspaceJustAddedTimer = null;
      this.closeFlowWorkspacePicker();
    }, 1500);
  }

  // ── Project-aware "Save to Workspace" auto-route ──────
  //
  // When the loaded meeting is tagged into a project that has a
  // linked Knowledge workspace (FlowProject ↔ Knowledge integration),
  // we skip the workspace picker entirely. The user already chose
  // their workspace by linking the project; asking again every time
  // would be friction. The chip label flips to "Save to <Name>" so
  // it's visually clear where the page will land.

  /** First project (in iteration order) attached to the loaded
   *  meeting that has a linked Knowledge workspace + folder. Returns
   *  null when none — the user falls through to the regular picker. */
  protected readonly linkedWorkspaceForRouting = computed<
    { workspaceId: string; workspaceName: string } | null
  >(() => {
    for (const p of this.service.meetingProjects()) {
      if (p.knowledgeWorkspaceId && p.knowledgeFolderPageId) {
        return {
          workspaceId: p.knowledgeWorkspaceId,
          workspaceName: p.knowledgeWorkspaceName ?? p.name,
        };
      }
    }
    return null;
  });

  /** One-click "Save to <Workspace>" — no picker. Shows the same
   *  success affordance as the picker path so the UX feels consistent. */
  protected async commitFlowWorkspaceAutoRoute(
    flowId: string | null | undefined,
  ): Promise<void> {
    if (!flowId) return;
    const target = this.linkedWorkspaceForRouting();
    if (!target) return;
    const result = await this.service.addFlowToKnowledge(
      target.workspaceId,
      flowId,
    );
    if (!result?.success) return;
    if (this._flowWorkspaceJustAddedTimer) {
      clearTimeout(this._flowWorkspaceJustAddedTimer);
      this._flowWorkspaceJustAddedTimer = null;
    }
    // Open the picker dropdown briefly just so the success state has
    // somewhere to render. It auto-closes via the existing 1.5s timer.
    this._flowWorkspacePickerFlowId.set(flowId);
    this.flowWorkspaceJustAddedId.set(target.workspaceId);
    this._flowWorkspaceJustAddedTimer = setTimeout(() => {
      this.flowWorkspaceJustAddedId.set(null);
      this._flowWorkspaceJustAddedTimer = null;
      this.closeFlowWorkspacePicker();
    }, 1500);
  }

  // ── Schedule V2 — handlers for the new atoms ────────────────────

  /**
   * Apply a meeting template to the draft. Routes through the service
   * so the seeding logic stays out of the panel. Also captures the
   * template id for the read-only audit trail.
   */
  onTemplateApplied(tpl: MeetingTemplate): void {
    this.service.applyMeetingTemplate(tpl);
  }

  onTemplateCleared(): void {
    this.service.clearMeetingTemplate();
  }

  onSaveAsTemplate(): void {
    const subject = this.service.subject().trim();
    if (!subject) return;
    const durationMin = this.service.duration() ?? 30;
    const primary = this.service.rooms().find(r => r.isPrimary);
    const name = subject.length > 60 ? subject.slice(0, 60) : subject;
    void this.userLibrary.saveBookingAsTemplate({
      name,
      defaults: {
        durationMin,
        layoutKey: primary?.layoutKey ?? null,
        video: this.service.isOnlineMeeting(),
        visibility: this.service.isPrivate() ? 'private' : 'public',
      },
      categoryId: this.service.meetingCategoryId() ?? undefined,
    });
    this.analytics.capture(ANALYTICS_EVENTS.BMP_TEMPLATE_SAVED, {
      template_name: name,
    });
  }

  onCategoryPicked(id: string | null): void {
    this.service.setMeetingCategory(id);
  }

  onTagsChanged(tags: string[]): void {
    this.service.setMeetingTags(tags);
  }

  // ── Classify-row Flow handlers (create-mode) ──────────────────
  //
  // The bmp-classify-row component emits flowProjectChanged when the
  // user picks (or un-picks) a project in its inline popover. In
  // create-mode this writes to the form-state signal that feeds the
  // createBooking payload.
  protected onClassifyFlowChanged(projectId: string | null): void {
    this.service.flowProjectId.set(projectId);
  }

  protected async onClassifyNewProject(name: string): Promise<void> {
    const created = await this.flowProjects.createProject({ name });
    if (created) this.service.flowProjectId.set(created._id);
  }

  /** Lazy-load the viewer's FlowProjects list when the Add to Flow
   *  picker first opens. Belt-and-braces: the service.load() call in
   *  loadForView pre-loads in view-mode, but create-mode openings
   *  haven't run that path, and `load()` is idempotent so calling
   *  again is cheap. */
  protected onClassifyFlowPickerOpened(): void {
    void this.flowProjects.load();
  }

  // ── View-mode classify-row handlers ───────────────────────────
  //
  // In view-mode, the classify-row's writes route through the
  // viewer-overlay save mutations so the changes are private to the
  // viewer and never written to the source Booking. Optimistic on
  // the relevant signals so the chip + tag cluster + Flow pill
  // update immediately; the mutation persists in the background.

  /** Pick the single Flow project to display in the view-mode
   *  classify-row pill. View-mode supports multiple flow projects
   *  via meetingProjects[], but the pill is single-select — fall
   *  back to the first attached project, or null. */
  protected readonly viewModeActiveFlowProjectId = computed<string | null>(
    () => this.service.meetingProjects()[0]?._id ?? null,
  );

  protected onViewClassifyCategoryChanged(id: string | null): void {
    // Optimistic local signal update so the chip reflects the choice
    // before the network round-trip lands.
    this.service.meetingCategoryId.set(id);
    const eventId = this.service.loadedMeetingId();
    if (eventId) void this.service.saveOverlayCategory(eventId, id);
  }

  protected onViewClassifyTagsChanged(tags: string[]): void {
    this.service.meetingTags.set(tags);
    const eventId = this.service.loadedMeetingId();
    if (eventId) void this.service.saveOverlayTags(eventId, tags);
  }

  protected async onViewClassifyFlowChanged(projectId: string | null): Promise<void> {
    if (projectId === null) {
      // Detach whatever's currently attached (single-select pill).
      const current = this.viewModeActiveFlowProjectId();
      if (current) await this.service.detachProjectFromOverlay(current);
      return;
    }
    await this.service.attachProjectToOverlay(projectId);
  }

  protected async onViewClassifyNewProject(name: string): Promise<void> {
    const created = await this.flowProjects.createProject({ name });
    if (created) await this.service.attachProjectToOverlay(created._id);
  }

  onRoomLayoutPicked(spaceId: string, layoutKey: LayoutKey): void {
    this.service.setRoomLayout(spaceId, layoutKey === 'auto' ? null : layoutKey);
  }

  /** Schedule V2 alias for `onRoomLayoutPicked` — the new room-card
   *  template wires `(layoutPicked)="onLayoutPicked(...)"` to mirror
   *  the prototype's naming. Forwards straight through. */
  onLayoutPicked(spaceId: string, layoutKey: LayoutKey): void {
    this.onRoomLayoutPicked(spaceId, layoutKey);
  }

  /** Change role (overflow/satellite) for a room. The 'primary'
   *  sentinel is intrinsic to the existing primary so we ignore it. */
  onRoleChange(spaceId: string, role: string): void {
    if (role === 'overflow' || role === 'satellite') {
      this.service.setRoomRole(spaceId, role);
    }
  }

  /** Open the inline room picker so the user can swap in a different
   *  room. Focuses the typeahead input on the next microtask so the
   *  next keypress starts a search. */
  onChangeRoom(_spaceId: string): void {
    this.showRoomPicker.set(true);
    queueMicrotask(() => {
      try {
        const host = this.el.nativeElement as HTMLElement;
        const input = host.querySelector('im-room-picker-inline input') as HTMLInputElement | null;
        input?.focus();
      } catch { /* no-op */ }
    });
  }

  /** Remove a (non-primary) room from the booking. Thin alias around
   *  the existing service mutator so the new room-card template stays
   *  free of `service.` prefixes for clarity. */
  onRemoveRoom(spaceId: string): void {
    this.service.removeRoom(spaceId);
  }

  /** Open the catering picker for the given room on the active day. */
  openCateringDialog(room: RoomSelection): void {
    this.service.openCateringForRoom(room, this.activeDayForRoom(room.spaceId));
  }

  /** Layout key currently chosen for a room — declarative wrapper. */
  roomLayout(room: RoomSelection): LayoutKey {
    return this.resolveLayoutKey(room.layoutKey);
  }

  /** Surface the current role for a room. Returns 'primary' for the
   *  primary room (the role select sentinel), falling back to
   *  'overflow' otherwise so the dropdown reflects a concrete value. */
  roomRole(room: RoomSelection): 'primary' | 'overflow' | 'satellite' {
    if (room.isPrimary) return 'primary';
    return room.roomRole ?? 'overflow';
  }

  /** Catering drafts attached to a room — proxy to the service. */
  roomCateringOrders(room: RoomSelection): CateringOrderDraft[] {
    return this.service.ordersForRoom(room.spaceId);
  }

  /** Optional star rating displayed in the meta row. RoomSelection
   *  doesn't yet carry a rating field; surfaced via the room's
   *  optional `rating` property when present. Returns null otherwise
   *  so the template hides the star glyph. */
  roomRating(room: RoomSelection): number | null {
    // RoomSelection.rating may be a number (legacy) OR a RoomMetaRating
    // object (post-hydrate). Normalise to a single average value, only
    // returning when there's at least one review backing it.
    const r = (room as { rating?: number | { averageRating: number; totalRatings: number } }).rating;
    if (typeof r === 'number') return r;
    if (r && r.totalRatings > 0) return r.averageRating;
    return null;
  }

  /** Number of open service issues against the room, hydrated from the
   *  Space query. Returns 0 when unknown so the chip stays hidden. */
  roomOpenIssues(room: RoomSelection): number {
    return room.issues?.openCount ?? 0;
  }

  /** Per-card tooltip state — drives the CDK Overlay popovers for
   *  Rating and Issues chips. Tracks (roomId, kind) so only one
   *  tooltip is open at a time across all rooms. */
  protected readonly hoveredTooltip = signal<{
    roomId: string;
    kind: 'rating' | 'issues';
  } | null>(null);

  /** CDK Overlay positions for the rich tooltips. Tries below-start,
   *  then above-start as a fallback when the card is near the bottom
   *  of the viewport. */
  protected readonly tooltipPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  protected onShowTooltip(roomId: string, kind: 'rating' | 'issues'): void {
    this.hoveredTooltip.set({ roomId, kind });
  }
  protected onHideTooltip(): void {
    this.hoveredTooltip.set(null);
  }
  protected isTooltipOpen(roomId: string, kind: 'rating' | 'issues'): boolean {
    const t = this.hoveredTooltip();
    return !!t && t.roomId === roomId && t.kind === kind;
  }

  /** Coerce a RoomSelection.layoutKey (which may be any space-layout
   *  string or undefined) into the LayoutKey enum the bmp-layout-picker
   *  understands. Unknown values fall back to 'auto'. */
  resolveLayoutKey(value: string | undefined): LayoutKey {
    const known: LayoutKey[] = [
      'auto', 'boardroom', 'theatre', 'classroom', 'ushape', 'standing',
    ];
    if (!value) return 'auto';
    return known.includes(value as LayoutKey) ? (value as LayoutKey) : 'auto';
  }

  /** The FlowProject currently linked to the meeting, or null when
   *  nothing is chosen. Drives the chip rendering in the classify
   *  row's Flow pill. */
  protected readonly activeFlowProject = computed(() => {
    const id = this.service.flowProjectId();
    if (!id) return null;
    return this.flowProjects.activeProjects().find(p => p._id === id) ?? null;
  });

  // ── Create-mode Flow picker (single-select) ────────────────────────
  //
  // Mirrors the polished view-mode picker (`projectPicker*`) but for
  // the create-mode case where we just need to set
  // `service.flowProjectId` instead of calling addToProject (which
  // requires a loaded calendar meeting id). Same SCSS classes.
  protected readonly flowPickerOpen = signal(false);
  protected readonly flowPickerQuery = signal('');
  protected readonly flowPickerCreating = signal(false);
  protected readonly flowPickerNewName = signal('');

  protected readonly flowPickerSuggestions = computed(() => {
    const q = this.flowPickerQuery().trim().toLowerCase();
    const all = this.flowProjects.activeProjects();
    if (!q) return all;
    return all.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  });

  protected async onOpenFlowPicker(): Promise<void> {
    await this.flowProjects.load();
    this.flowPickerOpen.set(true);
    this.flowPickerQuery.set('');
    this.flowPickerCreating.set(false);
    this.flowPickerNewName.set('');
  }

  protected closeFlowPicker(): void {
    this.flowPickerOpen.set(false);
    this.flowPickerQuery.set('');
    this.flowPickerCreating.set(false);
    this.flowPickerNewName.set('');
  }

  protected onPickFlowProject(projectId: string): void {
    const current = this.service.flowProjectId();
    this.service.flowProjectId.set(current === projectId ? null : projectId);
    this.closeFlowPicker();
  }

  protected async commitNewFlowProject(): Promise<void> {
    const name = this.flowPickerNewName().trim();
    if (!name) return;
    const created = await this.flowProjects.createProject({ name });
    if (!created) return;
    this.service.flowProjectId.set(created._id);
    this.closeFlowPicker();
  }

  onAttendeeRolePicked(
    email: string,
    role: 'host' | 'organiser' | 'attendee',
  ): void {
    this.service.setAttendeeRole(email, role);
  }

  onAttendeePresencePicked(
    email: string,
    presence: 'onsite' | 'remote',
  ): void {
    this.service.setAttendeePresence(email, presence);
  }

  // ── AI side-rail handlers — patch the draft with the model's output ─

  onAgendaDrafted(html: string): void {
    // The side-rail now converts the model's Markdown to HTML before
    // emitting so the rich-text editor renders lists / tables natively.
    // Seed the description with the agenda unless the user has already
    // typed one; otherwise append after a paragraph divider.
    if (!this.service.description().trim()) {
      this.service.description.set(html);
    } else {
      this.service.description.update(d => d + '<hr/>' + html);
    }
  }

  /**
   * AI side-rail "Suggest a title" chip. Force-fires the subject
   * suggestion (the auto-fire effect only runs when the subject is
   * blank — explicit click should work even when the user has typed
   * something). The suggestion is left in `aiSubjectSuggestion` so
   * the AI rail surfaces it as a result card the user explicitly
   * accepts or dismisses — auto-apply was confusing because the user
   * never saw an action take place.
   */
  async onSuggestTitleRequested(): Promise<void> {
    await this.service.loadSubjectSuggestion();
    this.analytics.capture(ANALYTICS_EVENTS.BMP_AI_ASSIST_USED, {
      kind: 'suggest-title',
      accepted: false,
    });
  }

  /** Accept the surfaced title suggestion from the AI rail. */
  onAcceptTitleSuggestion(): void {
    const had = !!this.service.aiSubjectSuggestion();
    this.service.acceptSubjectSuggestion();
    if (had) {
      this.analytics.capture(ANALYTICS_EVENTS.BMP_AI_ASSIST_USED, {
        kind: 'suggest-title',
        accepted: true,
      });
    }
  }

  /** Dismiss the surfaced title suggestion from the AI rail. */
  onDismissTitleSuggestion(): void {
    this.service.dismissSubjectSuggestion();
  }

  onSummariseLastRequested(): void {
    void this.service.loadLastMeetingSummary();
    this.analytics.capture(ANALYTICS_EVENTS.BMP_AI_ASSIST_USED, {
      kind: 'summarise',
      accepted: false,
    });
  }

  /** Prepend the AI-generated last-meeting summary to the agenda field
   *  with a horizontal-rule divider so the user can review and edit
   *  before sending. */
  onUseAsAgenda(summary: string): void {
    if (!summary) return;
    const current = this.service.description() ?? '';
    const divider = current.trim().length > 0 ? '\n\n---\n\n' : '';
    this.service.description.set(summary + divider + current);
    this.analytics.capture(ANALYTICS_EVENTS.BMP_AI_ASSIST_USED, {
      kind: 'suggest-agenda',
      accepted: true,
    });
    // Dismiss the result card after the user accepts so the rail
    // doesn't keep showing a stale summary they've already applied.
    this.service.lastMeetingSummary.set(null);
  }

  /** Clear the result card without applying anything. */
  onDismissLastSummary(): void {
    this.service.lastMeetingSummary.set(null);
  }

  onSimilarPicked(s: SimilarMeeting): void {
    // Apply the prior meeting's title + duration. We don't copy
    // attendees because the user is mid-flow with their own list.
    if (!this.service.subject().trim()) {
      this.service.subject.set(s.title);
    }
    const start = this.service.startTime();
    if (start && s.durationMin > 0) {
      this.service.endTime.set(
        new Date(start.getTime() + s.durationMin * 60_000),
      );
    }
    this.analytics.capture(ANALYTICS_EVENTS.BMP_AI_ASSIST_USED, {
      kind: 'similar-meeting',
      accepted: true,
    });
  }

  // ── Schedule V2 — derived signals for the AI side rail + footer summary

  /** Attendee emails passed into the AI side rail. Computed, not a
   *  signal, so changes drive `bmp-ai-side`'s `effect()` reactively. */
  protected readonly aiSideAttendeeEmails = computed(() =>
    this.service.attendees().map(a => a.email).filter(Boolean),
  );

  protected readonly aiSideAttendeeNames = computed(() =>
    this.service.attendees().map(a => a.name).filter(Boolean),
  );

  /**
   * Combined notes blob the AI agenda call grounds on. Includes any
   * already-typed public agenda (so the user can re-draft over a
   * partial outline) AND the viewer's private notes (talking points,
   * prior-meeting prep). Empty string when both are blank.
   */
  protected readonly aiAgendaNotes = computed(() => {
    const stripHtml = (html: string): string =>
      html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .trim();
    const agenda = stripHtml(this.service.description() ?? '');
    const notes = (this.service.internalNotes() ?? '').trim();
    if (!agenda && !notes) return null;
    if (agenda && notes) return `Existing agenda draft:\n${agenda}\n\nPrivate prep notes:\n${notes}`;
    return agenda || notes;
  });

  /**
   * Flow project context blob the AI agenda call grounds on. Resolves
   * to {name, recentTopics, openQuestions} when the meeting is
   * attached to a Flow project so the model treats this draft as a
   * continuation rather than a one-off. Null when no project linked.
   */
  protected readonly aiFlowProjectContext = computed(() => {
    const proj = this.activeFlowProject();
    if (!proj) return null;
    return {
      name: proj.name ?? undefined,
      // FlowProject doesn't carry topic / question signals directly in
      // its summary shape — fall back to the project description (when
      // present) as a single "recent topic" entry so the model gets at
      // least the project's framing. Future iteration could resolve
      // these from a richer projectDetail query.
      recentTopics: proj.description ? [proj.description] : undefined,
      openQuestions: undefined,
    };
  });

  /** Minutes-since-midnight of the booking start — drives the footer
   *  summary's "9:00am" label. Returns 0 when no start is set. */
  protected readonly footerStartMin = computed(() => {
    const d = this.service.startTime();
    if (!d) return 0;
    return d.getHours() * 60 + d.getMinutes();
  });

  /** Total number of catering orders across all rooms in the booking. */
  protected readonly footerCateringCount = computed(() => {
    let count = this.service.cateringOrders().length;
    for (const room of this.service.rooms()) {
      count += room.cateringOrders?.length ?? 0;
    }
    return count;
  });

  /**
   * Per-attendee busy bands for the find-time strip, computed from the
   * SchedulingAssistantService.peopleSchedules signal. Returns the
   * viewer-shaped projection (`FindTimeAttendee`) so the strip stays
   * pure presentational.
   *
   * The peopleAvailability query is fired by other parts of the panel
   * (the conflict-pill effect already in place); we just project what's
   * cached. Status mapping: 0=free, 1=tentative, 2=busy, 3=oof.
   */
  protected readonly findTimeAttendees = computed<FindTimeAttendee[]>(() => {
    const attendees = this.service.attendees();
    const schedules = this.scheduling.peopleSchedules();
    if (!attendees.length) return [];

    return attendees.map(a => {
      const sched = schedules.find(s => s.email.toLowerCase() === a.email.toLowerCase());
      const items = sched?.scheduleItems ?? [];
      // Collapse every non-free status (busy + tentative + oof) into a
      // single "busy" band per contiguous range. Per the user direction
      // — the strip is a binary "busy vs free" view, not a status
      // breakdown. Severity nuance still surfaces in the band tooltip
      // (we keep the worst-status label) but visually it's one block.
      const rawBusy = items
        .filter(s => s.status === 'busy' || s.status === 'tentative' || s.status === 'oof')
        .map(s => ({
          start: this.minSinceMidnight(s.start),
          end: this.minSinceMidnight(s.end),
        }))
        .filter(b => b.end > b.start);

      // Bucket into 15-min slots — any non-free slot is "busy".
      // Merge consecutive busy slots into single thick bands.
      const SLOT = 15;
      const DAY_START = 7 * 60;
      const DAY_END = 19 * 60;
      const slotCount = (DAY_END - DAY_START) / SLOT;
      const slotBusy: boolean[] = new Array(slotCount).fill(false);
      for (const b of rawBusy) {
        const startSlot = Math.max(0, Math.floor((b.start - DAY_START) / SLOT));
        const endSlot = Math.min(slotCount, Math.ceil((b.end - DAY_START) / SLOT));
        for (let i = startSlot; i < endSlot; i++) slotBusy[i] = true;
      }
      const merged: Array<{ start: number; end: number; status: 'busy' }> = [];
      let i = 0;
      while (i < slotCount) {
        if (!slotBusy[i]) { i++; continue; }
        let j = i;
        while (j < slotCount && slotBusy[j]) j++;
        merged.push({
          start: DAY_START + i * SLOT,
          end: DAY_START + j * SLOT,
          status: 'busy',
        });
        i = j;
      }

      return {
        email: a.email,
        name: a.name,
        color: 'var(--color-primary)',
        pictureUrl: a.picture || undefined,
        busy: merged,
      };
    });
  });

  private minSinceMidnight(iso: string): number {
    if (!iso) return 0;
    // Naive datetime strings ("2026-05-18T09:00:00") — split out the HH:mm part
    // directly to avoid the timezone bug captured in
    // memory `reference_scheduler_timezone_bug` (don't use Date.getHours()).
    const match = iso.match(/T(\d{2}):(\d{2})/);
    if (match) return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    const d = new Date(iso);
    return d.getHours() * 60 + d.getMinutes();
  }

  /** "Better times" — derived from the existing peopleSchedules + the
   *  primary room's events. Mirrors the prototype's better-time pills. */
  protected readonly findTimeBetterTimes = computed<FindTimeBetterSlot[]>(() => {
    const attendees = this.findTimeAttendees();
    if (!attendees.length) return [];

    const start = this.service.startTime();
    const duration = this.service.duration() ?? 30;
    if (!start) return [];

    const meetingStart = start.getHours() * 60 + start.getMinutes();
    // Scan the full work day (7am–7pm) for suggestions, ranked by
    // proximity to the proposed time. This matches the strip's
    // horizontal window — the user can see and pick any of them.
    const windowStart = 7 * 60;
    const windowEnd = 19 * 60;

    // Try every 15-min step in the window, pick the 3 nearest to
    // meetingStart where ALL attendees are free.
    const candidates: FindTimeBetterSlot[] = [];
    const primary = this.service.rooms().find(r => r.isPrimary);
    for (let t = windowStart; t + duration <= windowEnd; t += 15) {
      if (t === meetingStart) continue; // skip the current slot
      const free = attendees.every(p =>
        p.busy.every(b => !(b.start < t + duration && b.end > t)),
      );
      if (free) {
        candidates.push({
          startMin: t,
          endMin: t + duration,
          roomName: primary?.name,
        });
      }
    }
    candidates.sort(
      (a, b) =>
        Math.abs(a.startMin - meetingStart) - Math.abs(b.startMin - meetingStart),
    );
    return candidates.slice(0, 3);
  });

  onFindTimePicked(slot: FindTimeBetterSlot): void {
    const start = this.service.startTime();
    if (!start) return;
    const newStart = new Date(start);
    newStart.setHours(Math.floor(slot.startMin / 60), slot.startMin % 60, 0, 0);
    const newEnd = new Date(newStart.getTime() + (slot.endMin - slot.startMin) * 60_000);
    const duration = Math.round((slot.endMin - slot.startMin));
    this.analytics.capture(ANALYTICS_EVENTS.BMP_FIND_TIME_PICKED, {
      was_recommended: !!(slot as { recommended?: boolean }).recommended,
      start_time: newStart.toISOString(),
      duration_minutes: duration,
    });
    this.service.startTime.set(newStart);
    this.service.endTime.set(newEnd);
  }

  /** Sum of all catering totals across all rooms. */
  protected readonly footerCateringTotal = computed(() => {
    let total = 0;
    for (const order of this.service.cateringOrders()) {
      total += (order as unknown as { totalAmount?: number }).totalAmount ?? 0;
    }
    for (const room of this.service.rooms()) {
      for (const order of room.cateringOrders ?? []) {
        total += order.total;
      }
    }
    return total;
  });

  onSuggestedRoomPicked(room: RoomsGroupUsesRoom): void {
    // Apply as primary room if there's nothing else picked; otherwise
    // do nothing (the user is already actively choosing). The full
    // multi-room overflow flow is owned by the Rooms tab's typeahead.
    if (this.service.rooms().length === 0) {
      this.service.rooms.set([
        {
          spaceId: room.spaceId,
          name: room.name,
          locationId: '',
          floorName: room.floorName ?? undefined,
          capacity: room.capacity ?? undefined,
          isPrimary: true,
        },
      ]);
    }
  }
}

/**
 * Convert a snake_case / camelCase JSON key into a human-readable
 * heading. Used by `detailedSummaryBlocks` to render Gemini's
 * structured summary keys (action_items → "Action items", keyTopics
 * → "Key topics") without hard-coding the schema.
 */
function humaniseKey(key: string): string {
  if (!key) return '';
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
